import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Drawer,
  FormControl, Grid, IconButton, InputLabel, LinearProgress, MenuItem,
  Paper, Select, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddTaskIcon from '@mui/icons-material/AddTask';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid } from '@mui/x-data-grid';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement,
  Title, Tooltip as ChartTooltip, Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, ChartTooltip, Legend);

const VPS_API = window.location.hostname.includes('localhost')
  ? 'http://localhost:3009'
  : '';

const BRAND_META = {
  'LEC Magazine — ladiesentrepreneurshipclub.com': { url: 'ladiesentrepreneurshipclub.com', short: 'LEC', desc: 'Online magazine + community for female entrepreneurs' },
  'ladiesentrepreneurshipclub.com': { url: 'ladiesentrepreneurshipclub.com', short: 'LEC', desc: 'Online magazine + community for female entrepreneurs' },
  'tonishatagoe.com': { url: 'tonishatagoe.com', short: 'TT', desc: 'Personal brand + publishing platform' },
  'applesandpears.biz': { url: 'applesandpears.biz', short: 'A&P', desc: 'Main company website + WooCommerce store' },
  'Data Insights': { url: 'di.applesandpears.biz', short: 'DI', desc: 'Internal analytics + agent ops dashboard' },
  'Oasis Mail': { url: 'oasis.applesandpears.biz', short: 'OA', desc: 'Personal daily operating system + AI assistant' },
  'Domino': { url: '', short: 'DO', desc: 'AI-powered campaign management platform' },
  'APH Booking Plugin': { url: '', short: 'BK', desc: 'WooCommerce booking plugin for all APH sites' },
};

const getBrandMeta = (project) => {
  const exact = BRAND_META[project];
  if (exact) return exact;
  for (const [key, val] of Object.entries(BRAND_META)) {
    if (project.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(project.toLowerCase())) return val;
  }
  return { url: '', short: project.slice(0, 2).toUpperCase(), desc: '' };
};

const BRAND_COLORS = ['#002B45','#1565c0','#2e7d32','#6a1b9a','#00695c','#ad1457','#e65100','#37474f'];

const PORTFOLIO_BRANDS = {
  'Apples & Pears': {
    short: 'A&P', color: '#002B45', url: 'applesandpears.biz',
    desc: 'Main brand + WooCommerce store + all APH WordPress plugins',
    goals: ['WooCommerce store live + converting', 'All APH plugins deployed & integrated', 'Booking + CRM working across all sites', 'Member area (Executive Hub) live'],
    aliases: ['Applesandpears Biz','Apples Pears','Apples And Pears Web Projects','Aph Ecosystem','Aph Business',
      'Aph Wordpress Sites','applesandpears.biz','APH Booking Plugin','Aph Booking Plugin','Aph Crm Aph Reach',
      'Apples Pears Crm','APH CRM / Reach','Aph Multi Site','APH Multisite','Aph Wordpress Maintenance',
      'APH WP Maintenance','aph-c-suite','APH C-Suite','APH Executive Hub','A P My Account','APH My Account',
      'Apples & Pears','Apples and Pears','APH Booking','APH CRM','APH My Account'],
  },
  'Ladies Entrepreneurship Club': {
    short: 'LEC', color: '#6a1b9a', url: 'ladiesentrepreneurshipclub.com',
    desc: 'LEC Magazine — content platform for women entrepreneurs',
    goals: ['Full magazine redesign live', 'TV section + content hub live', 'SEO content pipeline automated', 'Community + events area'],
    aliases: ['Ladies Entrepreneurship Club','Lec Website','Lec Magazine Ladiesentrepreneurshipclub Com',
      'LEC Magazine — ladiesentrepreneurshipclub.com','New LEC Website','ladiesentrepreneurshipclub.com',
      'LEC Magazine','Lec Magazine','LEC'],
  },
  'Tonisha Tagoe': {
    short: 'TT', color: '#ad1457', url: 'tonishatagoe.com',
    desc: "Tonisha's personal brand site + SOVEREIGN AI governance framework",
    goals: ['SOVEREIGN live on TT WooCommerce', 'Baobab Intelligence plugin deployed to all sites', 'Personal brand content hub live', 'Books + media portfolio page'],
    aliases: ['tonishatagoe.com','Tonisha Tagoe Website','Tonisha Tagoe Ecosystem','Tonishatagoe Com',
      'Tt Ap Websites','Baobab Intelligence Plugin','Tonisha Books Baobab Intelligence','Baobab Intelligence',
      'Bi Plugin','Sovereign','SOVEREIGN','Tonisha Tagoe'],
  },
  'ini': {
    short: 'IN', color: '#e65100', url: '',
    desc: 'ini — brand in development',
    goals: ['Define brand positioning', 'Launch website', 'Build product suite', 'Go to market'],
    aliases: ['ini','Ini'],
  },
  'Domino': {
    short: 'DO', color: '#1565c0', url: '',
    desc: 'AI-powered campaign management — Electron + Web + Capacitor',
    goals: ['Campaign builder live', 'Multi-agent coordination', 'Outreach scheduling', 'Analytics dashboard'],
    aliases: ['Domino'],
  },
  'AI Excellence Hub': {
    short: 'AE', color: '#4a148c', url: '',
    desc: 'Online learning platform for AI excellence training',
    goals: ['Course delivery live', 'Student progress tracking', 'Certificate generation', 'Payment integration'],
    aliases: ['AI Excellence Hub','Ai Excellence Hub'],
  },
};

const PORTFOLIO_ALIAS_MAP = (() => {
  const m = {};
  Object.entries(PORTFOLIO_BRANDS).forEach(([brand, meta]) => {
    m[brand.toLowerCase()] = brand;
    (meta.aliases || []).forEach(a => { m[a.toLowerCase()] = brand; });
  });
  return m;
})();

// Maps messy project name variants from Oasis/chat logs → canonical name
const PROJECT_ALIASES = {
  // Baobab
  'Baobab Intelligence Plugin': 'Baobab Intelligence',
  'Tonisha Books Baobab Intelligence': 'Baobab Intelligence',
  'Baobab Intelligence': 'Baobab Intelligence',
  'Bi Plugin': 'Baobab Intelligence',
  'baobab-intelligence': 'Baobab Intelligence',
  'baobab-intelligence-pro': 'Baobab Intelligence Pro',
  'Baobab Intelligence Pro': 'Baobab Intelligence Pro',
  'baobab-intelligence-forms': 'Baobab Intelligence Forms',
  'Baobab Intelligence Forms': 'Baobab Intelligence Forms',
  'bi-funnels': 'BI Funnels',
  'BI Funnels': 'BI Funnels',
  // APH Core
  'APH Booking Plugin': 'APH Booking Plugin',
  'Aph Booking Plugin': 'APH Booking Plugin',
  'aph-bookings': 'APH Booking Plugin',
  'aph-booking': 'APH Booking Plugin',
  'Sovereign': 'SOVEREIGN',
  'Aph Crm Aph Reach': 'APH CRM / Reach',
  'Apples Pears Crm': 'APH CRM / Reach',
  'aph-crm': 'APH CRM / Reach',
  'aph-reach': 'APH CRM / Reach',
  'Aph Multi Site': 'APH Multisite',
  'Aph Wordpress Maintenance': 'APH WP Maintenance',
  'aph-c-suite': 'APH Executive Hub',
  'APH C-Suite': 'APH Executive Hub',
  'A P My Account': 'APH My Account',
  'aph-executive-hub': 'APH Executive Hub',
  'aph-my-account': 'APH My Account',
  'aph-events-pro': 'APH Events Pro',
  'APH Events Pro': 'APH Events Pro',
  'aph-events-pro-plus': 'APH Events Pro+',
  'APH Events Pro Plus': 'APH Events Pro+',
  'aph-magnets': 'APH Magnets',
  'APH Magnets': 'APH Magnets',
  'aph-page-templates': 'APH Page Templates',
  'APH Page Templates': 'APH Page Templates',
  'aph-site-essentials': 'APH Site Essentials',
  'APH Site Essentials': 'APH Site Essentials',
  'aph-ceo-dashboard': 'APH CEO Dashboard',
  'APH CEO Dashboard': 'APH CEO Dashboard',
  'aph-forms-surveys': 'APH Forms & Surveys',
  'APH Forms Surveys': 'APH Forms & Surveys',
  'aph-newsletter': 'APH Newsletter',
  'APH Newsletter': 'APH Newsletter',
  'aph-product-settings': 'APH Product Settings',
  'APH Product Settings': 'APH Product Settings',
  'aph-woocommerce-styles': 'APH WooCommerce Styles',
  'APH WooCommerce Styles': 'APH WooCommerce Styles',
  'aph-media-folders': 'APH Media Folders',
  'APH Media Folders': 'APH Media Folders',
  'aph-booknetic': 'APH Booknetic',
  'APH Booknetic': 'APH Booknetic',
  // LEC
  'lec-join': 'LEC Join',
  'LEC Join': 'LEC Join',
  'lec-platform': 'LEC Platform',
  'LEC Platform': 'LEC Platform',
  'aph-events-utils': 'APH Events Utils',
  'APH Events Utils': 'APH Events Utils',
  'lec-community-hub': 'LEC Community Hub',
  'LEC Community Hub': 'LEC Community Hub',
  'lec-magazine': 'LEC Magazine Plugin',
  'LEC Magazine Plugin': 'LEC Magazine Plugin',
  'lec-reviews': 'LEC Reviews',
  'LEC Reviews': 'LEC Reviews',
  'lec-shortcode-fix': 'LEC Shortcode Fix',
  'LEC Shortcode Fix': 'LEC Shortcode Fix',
  'lec-global-styles.php': 'LEC Global Styles',
  'lec-global-styles': 'LEC Global Styles',
  'LEC Global Styles': 'LEC Global Styles',
  'lec-cart-checkout-fix.php': 'LEC Cart Checkout Fix',
  'lec-cart-checkout-fix': 'LEC Cart Checkout Fix',
  'LEC Cart Checkout Fix': 'LEC Cart Checkout Fix',
  'lec-account-emails.php': 'LEC Account Emails',
  'lec-account-emails': 'LEC Account Emails',
  'LEC Account Emails': 'LEC Account Emails',
  'lec-event-enhancements.php': 'LEC Event Enhancements',
  'lec-event-enhancements': 'LEC Event Enhancements',
  'LEC Event Enhancements': 'LEC Event Enhancements',
  'lec-product-router.php': 'LEC Product Router',
  'lec-product-router': 'LEC Product Router',
  'LEC Product Router': 'LEC Product Router',
  'lec-my-account-styles.php': 'LEC My Account Styles',
  'lec-my-account-styles': 'LEC My Account Styles',
  'LEC My Account Styles': 'LEC My Account Styles',
  'lec-mtf-template.php': 'LEC MTF Template',
  'lec-mtf-template': 'LEC MTF Template',
  'LEC MTF Template': 'LEC MTF Template',
  'lec-ceo-dashboard.php': 'LEC CEO Dashboard',
  'lec-ceo-dashboard': 'LEC CEO Dashboard',
  'LEC CEO Dashboard': 'LEC CEO Dashboard',
  'lec-reg-popup.php': 'LEC Registration Popup',
  'lec-reg-popup': 'LEC Registration Popup',
  'LEC Registration Popup': 'LEC Registration Popup',
  'lec-staged-content.php': 'LEC Staged Content',
  'lec-staged-content': 'LEC Staged Content',
  'LEC Staged Content': 'LEC Staged Content',
  'lec-raw-html-pages.php': 'LEC Raw HTML Pages',
  'lec-raw-html-pages': 'LEC Raw HTML Pages',
  'LEC Raw HTML Pages': 'LEC Raw HTML Pages',
  'lec-template-manager.php': 'LEC Template Manager',
  'lec-template-manager': 'LEC Template Manager',
  'LEC Template Manager': 'LEC Template Manager',
  'lec-lms-branding.php': 'LEC LMS Branding',
  'lec-lms-branding': 'LEC LMS Branding',
  'LEC LMS Branding': 'LEC LMS Branding',
  'lec-font-preload.php': 'LEC Font Preload',
  'lec-font-preload': 'LEC Font Preload',
  'LEC Font Preload': 'LEC Font Preload',
  'lec-functions-contact.php': 'LEC Functions Contact',
  'lec-functions-contact': 'LEC Functions Contact',
  'LEC Functions Contact': 'LEC Functions Contact',
  // TT
  'tt-books-store': 'TT Books Store',
  'TT Books Store': 'TT Books Store',
  'tonisha-custom-assets-v2.php': 'Tonisha Custom Assets V2',
  'tonisha-custom-assets-v2': 'Tonisha Custom Assets V2',
  'Tonisha Custom Assets V2': 'Tonisha Custom Assets V2',
  'tonisha-custom-assets.php': 'Tonisha Custom Assets',
  'tonisha-custom-assets': 'Tonisha Custom Assets',
  'Tonisha Custom Assets': 'Tonisha Custom Assets',
  'wc-templates': 'TT WC Templates',
  'TT WC Templates': 'TT WC Templates',
  // Shared
  'aph-checkout-payment-ui-fix.php': 'APH Checkout Payment UI Fix',
  'aph-checkout-payment-ui-fix': 'APH Checkout Payment UI Fix',
  'APH Checkout Payment UI Fix': 'APH Checkout Payment UI Fix',
  'aph-quantity-text-contrast-fix.php': 'APH Quantity Text Contrast Fix',
  'aph-quantity-text-contrast-fix': 'APH Quantity Text Contrast Fix',
  'APH Quantity Text Contrast Fix': 'APH Quantity Text Contrast Fix',
  'force-usd-paystack-availability.php': 'Force USD Paystack Availability',
  'force-usd-paystack-availability': 'Force USD Paystack Availability',
  'Force USD Paystack Availability': 'Force USD Paystack Availability',
  'paystack-ghs-conversion.php': 'Paystack GHS Conversion',
  'paystack-ghs-conversion': 'Paystack GHS Conversion',
  'Paystack GHS Conversion': 'Paystack GHS Conversion',
  // Apps
  'Ai Excellence Hub': 'AI Excellence Hub',
  'Signal or Noise': 'Signal or Noise',
  'Global Knowdledge System': 'Global Knowledge System',
  'ContentManagerPro': 'ContentManagerPro',
  'CM Plus': 'CM Plus',
  'pricing-tool': 'Pricing Tool',
};

const PLUGIN_META = {
  // ── APH For-Sale Plugins ────────────────────────────────────────────────
  'APH Booking Plugin':      { short: 'BK', category: 'APH', desc: 'WooCommerce booking product type + live scheduler for AP, LEC & TT', goals: ['Live scheduler on AP, LEC & TT', 'Booking modal with date/time picker', 'Prospect intake + 8-step nurture sequence', 'v2.0 with full CRM integration'], color: '#1565c0', tech: 'PHP / WooCommerce' },
  'APH Events Pro':          { short: 'EV', category: 'APH', desc: 'Full events management plugin — ticketing, RSVP, recurring events', goals: ['v7.x stable on AP + LEC', 'Recurring events', 'Ticket sales + WooCommerce checkout', 'APH Events Utils companion'], color: '#0277bd', tech: 'PHP / WooCommerce' },
  'APH Events Pro+':         { short: 'EV+', category: 'APH', desc: 'Events Pro addon — advanced features and integrations', goals: ['Deploy to LEC', 'Advanced filtering', 'Venue + organiser CPTs'], color: '#01579b', tech: 'PHP / WordPress' },
  'APH Magnets':             { short: 'MG', category: 'APH', desc: 'Lead magnet delivery + opt-in system for WordPress', goals: ['Deliver PDFs on opt-in', 'WooCommerce free product flow', 'Email sequence trigger'], color: '#00838f', tech: 'PHP / WordPress' },
  'APH Page Templates':      { short: 'PT', category: 'APH', desc: 'Reusable page template system for APH WordPress sites', goals: ['Template library with preview', 'One-click page creation', 'Elementor + block compatibility'], color: '#00695c', tech: 'PHP / WordPress' },
  'APH Site Essentials':     { short: 'SE', category: 'APH', desc: 'Core utilities — redirects, maintenance mode, performance helpers', goals: ['Maintenance mode on all sites', 'Redirect manager', 'Performance + caching hooks'], color: '#37474f', tech: 'PHP / WordPress' },
  'APH CRM / Reach':         { short: 'CR', category: 'APH', desc: 'Internal CRM + outreach plugin for contacts and campaigns', goals: ['Contact list management', 'Campaign pipeline', 'Integration with Oasis/Domino'], color: '#00695c', tech: 'PHP / WordPress' },
  'APH CEO Dashboard':       { short: 'CD', category: 'APH', desc: 'Admin-only CEO-level dashboard — revenue, leads, KPIs in wp-admin', goals: ['Revenue KPI widgets', 'Lead pipeline view', 'Multi-site rollup'], color: '#4527a0', tech: 'PHP / WordPress' },
  'APH Executive Hub':       { short: 'EH', category: 'APH', desc: 'Premium member area — FX pricing, gated content, c-suite features', goals: ['FX currency (GBP→GHS) pricing', 'Gated c-suite content', 'Executive dashboard'], color: '#ad1457', tech: 'PHP / WooCommerce' },
  'APH Forms & Surveys':     { short: 'FS', category: 'APH', desc: 'Custom forms + survey builder for APH sites', goals: ['Survey CPT + results', 'WooCommerce checkout forms', 'Export to CSV'], color: '#e65100', tech: 'PHP / WordPress' },
  'APH Newsletter':          { short: 'NL', category: 'APH', desc: 'Lightweight newsletter + broadcast system for APH', goals: ['Subscriber list management', 'HTML broadcast send', 'Unsubscribe + GDPR compliance'], color: '#2e7d32', tech: 'PHP / WordPress' },
  'APH Product Settings':    { short: 'PS', category: 'APH', desc: 'Extended WooCommerce product settings — licensing, delivery rules', goals: ['License key delivery', 'Download expiry rules', 'Per-product custom fields'], color: '#6a1b9a', tech: 'PHP / WooCommerce' },
  'APH WooCommerce Styles':  { short: 'WS', category: 'APH', desc: 'WooCommerce front-end style overrides and UX improvements', goals: ['Consistent cart/checkout UX', 'Mobile-first product pages', 'APH brand theme integration'], color: '#455a64', tech: 'PHP / WooCommerce' },
  'APH Media Folders':       { short: 'MF', category: 'APH', desc: 'Media library folder organiser for WordPress', goals: ['Folder tree in media library', 'Bulk move/rename', 'Cross-site sync'], color: '#5d4037', tech: 'PHP / WordPress' },
  'APH Booknetic':           { short: 'BN', category: 'APH', desc: 'Booknetic-based scheduling integration — APH flavour', goals: ['Appointment booking UI', 'Staff + service management', 'Email/SMS reminders'], color: '#00838f', tech: 'PHP / WordPress' },
  'APH My Account':          { short: 'MA', category: 'APH', desc: 'Custom WooCommerce My Account — orders, downloads, subscriptions', goals: ['Custom account tabs', 'SOVEREIGN download access', 'Order history with re-purchase'], color: '#e65100', tech: 'PHP / WooCommerce' },
  'APH WP Maintenance':      { short: 'MT', category: 'APH', desc: 'WordPress maintenance utilities — DB cleanup, updates, performance', goals: ['Automated DB cleanup', 'Update queuing', 'Performance monitoring'], color: '#607d8b', tech: 'PHP / WordPress' },
  // ── Baobab Intelligence Suite ────────────────────────────────────────────
  'Baobab Intelligence':     { short: 'BI', category: 'Baobab', desc: 'Book intelligence + gifting plugin — free tier, powers SOVEREIGN', goals: ['Gift flow with branded email', 'BI-Pro downloadable on A&P', 'Deploy to all 3 sites', 'Reading progress tracking'], color: '#2e7d32', tech: 'PHP / WooCommerce' },
  'Baobab Intelligence Pro': { short: 'BIP', category: 'Baobab', desc: 'BI Pro tier — advanced book analytics + coaching features', goals: ['Pro-tier feature set live', 'Upgrade flow from BI free', 'Coaching session scheduling'], color: '#1b5e20', tech: 'PHP / WooCommerce' },
  'Baobab Intelligence Forms': { short: 'BIF', category: 'Baobab', desc: 'BI form builder addon — intake forms for coaching/reading programmes', goals: ['Form CPT + builder', 'WooCommerce checkout injection', 'Submissions dashboard'], color: '#33691e', tech: 'PHP / WordPress' },
  'BI Funnels':              { short: 'BF', category: 'Baobab', desc: 'BI funnel builder — multi-step conversion flows for BI products', goals: ['Visual funnel editor', 'A/B testing', 'Conversion tracking'], color: '#558b2f', tech: 'PHP / WordPress' },
  'SOVEREIGN':               { short: 'SV', category: 'Baobab', desc: 'AI governance framework — premium downloadable product (PDF + worksheet)', goals: ['Product live on TT WooCommerce', 'Download permissions for all purchasers', 'Guest order-user linking'], color: '#6a1b9a', tech: 'PHP / WooCommerce' },
  // ── LEC-Specific Plugins ─────────────────────────────────────────────────
  'LEC Join':                { short: 'LJ', category: 'LEC', desc: 'LEC membership join wizard — modal + [lec_join] shortcode', goals: ['Join modal live on LEC', 'Membership tier selection', 'WooCommerce product linkage'], color: '#6a1b9a', tech: 'PHP / WordPress' },
  'LEC Platform':            { short: 'LP', category: 'LEC', desc: 'LEC site utilities — merged lec-links + platform helpers', goals: ['Platform utilities stable', 'lec-links merged in', 'Shortcode library'], color: '#7b1fa2', tech: 'PHP / WordPress' },
  'APH Events Utils':        { short: 'EU', category: 'LEC', desc: 'LEC companion plugin for APH Events Pro — import, CSV/JSON, event helpers', goals: ['WYSIWYG short descriptions', 'CSV and JSON import tools', 'Keep LEC event admin stable'], color: '#7e57c2', tech: 'PHP / WordPress' },
  'LEC Community Hub':       { short: 'LC', category: 'LEC', desc: 'LEC social hub — community feeds, profiles, social pages', goals: ['Community page live (post 92157)', 'Member profiles', 'Activity feed'], color: '#8e24aa', tech: 'PHP / WordPress' },
  'LEC Magazine Plugin':     { short: 'LM', category: 'LEC', desc: 'LEC magazine layout — article CPT, sections, TV integration', goals: ['Magazine layout v2 live', 'TV section integration', 'Article SEO pipeline'], color: '#9c27b0', tech: 'PHP / WordPress' },
  'LEC Reviews':             { short: 'LR', category: 'LEC', desc: 'LEC review system — member reviews CPT + display', goals: ['Review submission form', 'Star ratings', 'Moderation workflow'], color: '#ab47bc', tech: 'PHP / WordPress' },
  'LEC Shortcode Fix':       { short: 'LS', category: 'LEC', desc: 'Fallback shortcode bridge used only when the main join flow is unavailable', goals: ['Keep fallback inactive unless debugging', 'Preserve [lec_join] compatibility'], color: '#ba68c8', tech: 'PHP / WordPress' },
  'LEC Global Styles':       { short: 'GS', category: 'LEC', desc: 'LEC MU design system and global style layer', goals: ['Shared visual system on LEC', 'Keep site-wide CSS overrides stable'], color: '#6a1b9a', tech: 'PHP / WordPress MU' },
  'LEC Cart Checkout Fix':   { short: 'CF', category: 'LEC', desc: 'LEC cart and checkout reliability fixes deployed as MU plugin', goals: ['Stable checkout flow', 'Cart behaviour fixes on all key funnels'], color: '#8e24aa', tech: 'PHP / WooCommerce MU' },
  'LEC Account Emails':      { short: 'AE', category: 'LEC', desc: 'Account email and dashboard helper plugin for LEC', goals: ['Improve account communications', 'Support dashboard account UX'], color: '#9c27b0', tech: 'PHP / WordPress MU' },
  'LEC Event Enhancements':  { short: 'EE', category: 'LEC', desc: 'MU enhancements for event pages and CTA injection on LEC', goals: ['Event CTA injection', 'Better event page conversion flow'], color: '#7b1fa2', tech: 'PHP / WordPress MU' },
  'LEC Product Router':      { short: 'PR', category: 'LEC', desc: 'WooCommerce routing helper for LEC products and checkout paths', goals: ['Correct product routing', 'Reduce dead-end commerce flows'], color: '#8e24aa', tech: 'PHP / WooCommerce MU' },
  'LEC My Account Styles':   { short: 'AS', category: 'LEC', desc: 'LEC My Account styling overrides as MU plugin', goals: ['Branded account area', 'Consistent account UX'], color: '#ab47bc', tech: 'PHP / WooCommerce MU' },
  'LEC MTF Template':        { short: 'MT', category: 'LEC', desc: 'Meet the Founders template helper for LEC', goals: ['Stable MTF template rendering', 'Reusable founder page layout'], color: '#9c27b0', tech: 'PHP / WordPress MU' },
  'LEC CEO Dashboard':       { short: 'CD', category: 'LEC', desc: 'MU CEO/admin dashboard for LEC operational visibility', goals: ['Leadership dashboard access', 'Admin metrics shortcuts'], color: '#6a1b9a', tech: 'PHP / WordPress MU' },
  'LEC Registration Popup':  { short: 'RP', category: 'LEC', desc: 'Registration popup flow for lead capture and sign-up prompts', goals: ['Popup registration prompts', 'Higher visitor-to-member conversion'], color: '#8e24aa', tech: 'PHP / WordPress MU' },
  'LEC Staged Content':      { short: 'SC', category: 'LEC', desc: 'Staged content controls for previews and controlled publishing', goals: ['Safely preview staged pages', 'Controlled content rollouts'], color: '#7b1fa2', tech: 'PHP / WordPress MU' },
  'LEC Raw HTML Pages':      { short: 'RH', category: 'LEC', desc: 'Raw HTML page support for special layouts on LEC', goals: ['Support special raw pages', 'Preserve custom page rendering'], color: '#9c27b0', tech: 'PHP / WordPress MU' },
  'LEC Template Manager':    { short: 'TM', category: 'LEC', desc: 'Template management utilities for LEC page assembly', goals: ['Manage reusable templates', 'Reduce page build duplication'], color: '#ab47bc', tech: 'PHP / WordPress MU' },
  'LEC LMS Branding':        { short: 'LB', category: 'LEC', desc: 'MasterStudy/LMS branding overrides for LEC learning surfaces', goals: ['Consistent academy branding', 'Improved LMS presentation'], color: '#6a1b9a', tech: 'PHP / LMS MU' },
  'LEC Font Preload':        { short: 'FP', category: 'LEC', desc: 'Font preload performance helper for LEC', goals: ['Improve font loading', 'Reduce layout shift on key pages'], color: '#8e24aa', tech: 'PHP / Performance MU' },
  'LEC Functions Contact':   { short: 'FC', category: 'LEC', desc: 'Contact and helper utilities used across LEC theme flows', goals: ['Shared contact helpers', 'Keep theme utilities centralized'], color: '#9c27b0', tech: 'PHP / WordPress MU' },
  // ── Tonisha Tagoe Plugins ────────────────────────────────────────────────
  'TT Books Store':          { short: 'TB', category: 'TT', desc: 'WooCommerce digital book store for tonishatagoe.com', goals: ['Book product types live', 'Gifting with BI integration', 'Author royalty tracking'], color: '#ad1457', tech: 'PHP / WooCommerce' },
  'Tonisha Custom Assets V2': { short: 'TA2', category: 'TT', desc: 'Current TT custom asset layer for styling and front-end behaviours', goals: ['Keep TT asset layer stable', 'Support brand-specific front-end interactions'], color: '#c2185b', tech: 'PHP / WordPress MU' },
  'Tonisha Custom Assets':   { short: 'TA', category: 'TT', desc: 'Legacy TT custom assets layer still tracked in the plugin inventory', goals: ['Preserve legacy overrides until fully retired'], color: '#d81b60', tech: 'PHP / WordPress MU' },
  'TT WC Templates':         { short: 'WC', category: 'TT', desc: 'WooCommerce template overrides for the Tonisha Tagoe storefront', goals: ['Book storefront templates', 'Consistent checkout and product UI'], color: '#e91e63', tech: 'PHP / WooCommerce' },
  // ── Shared MU Plugins ─────────────────────────────────────────────────────
  'APH Checkout Payment UI Fix': { short: 'PF', category: 'Shared', desc: 'Shared MU fix for checkout payment UI on APH and LEC', goals: ['Stable checkout payment presentation', 'Consistent cross-site payment UI'], color: '#546e7a', tech: 'PHP / WooCommerce MU' },
  'APH Quantity Text Contrast Fix': { short: 'QC', category: 'Shared', desc: 'Shared accessibility fix for WooCommerce quantity text contrast', goals: ['Readable quantity controls across sites'], color: '#607d8b', tech: 'PHP / WooCommerce MU' },
  'Force USD Paystack Availability': { short: 'PU', category: 'Shared', desc: 'Shared payment routing helper to force USD Paystack availability when needed', goals: ['Keep USD checkout available where required'], color: '#455a64', tech: 'PHP / Payments MU' },
  'Paystack GHS Conversion': { short: 'PG', category: 'Shared', desc: 'Shared GHS conversion helper for Paystack checkout pricing', goals: ['Reliable GHS conversion on checkout flows'], color: '#78909c', tech: 'PHP / Payments MU' },
};

const PLUGIN_CATEGORY_ORDER = ['APH', 'Baobab', 'LEC', 'TT', 'Shared'];

const APP_META = {
  'Oasis Mail':           { short: 'OA', desc: 'Personal daily operating system — Flask + vanilla JS, single-user', goals: ['Unified inbox + calendar', 'Isha AI assistant', 'Agent work tracking', 'Task + project management'], color: '#002B45', tech: 'Python / Flask', url: 'oasis.applesandpears.biz' },
  'Domino':               { short: 'DO', desc: 'AI-powered campaign management — Electron + Web + Capacitor', goals: ['Campaign builder', 'Multi-agent coordination', 'Outreach scheduling', 'Analytics'], color: '#1565c0', tech: 'Electron / React', url: '' },
  'Coda':                 { short: 'CO', desc: 'Coda-sourced docs, tables, and JSON export workflows surfaced inside Data Insights', goals: ['Coda doc exports', 'JSON table generation', 'Readable source browsing', 'Structured data handoff'], color: '#00897b', tech: 'Coda / JSON', url: '' },
  'AI Excellence Hub':    { short: 'AE', desc: 'Online learning platform for AI excellence training', goals: ['Course delivery', 'Student progress tracking', 'Certificate generation', 'Payment integration'], color: '#6a1b9a', tech: 'Flask / PostgreSQL', url: '' },
  'Data Insights':        { short: 'DI', desc: 'Internal analytics + agent ops dashboard', goals: ['Agent work tracking', 'Company portfolio view', 'Chat session history', 'KPI monitoring'], color: '#00695c', tech: 'React / Node.js', url: 'di.applesandpears.biz' },
  'Signal or Noise':      { short: 'SN', desc: 'Content signal analysis + market intelligence tool', goals: ['Signal detection pipeline', 'Content scoring', 'Dashboard'], color: '#37474f', tech: 'Python / React', url: '' },
  'Global Knowledge System': { short: 'GK', desc: 'Cross-project knowledge base — structured capture of research and decisions', goals: ['Knowledge ingestion pipeline', 'Search + retrieval', 'Agent-readable format'], color: '#455a64', tech: 'Python / Vector DB', url: '' },
  'ContentManagerPro':    { short: 'CM', desc: 'Content management + scheduling for all APH properties', goals: ['Content calendar', 'Multi-site publishing', 'SEO automation'], color: '#ad1457', tech: 'React / Node.js', url: '' },
  'Pricing Tool':         { short: 'PT', desc: 'Pricing calculator + FX conversion tool for APH products', goals: ['GBP→GHS conversion', 'Multi-currency support', 'Embeddable widget'], color: '#e65100', tech: 'React', url: '' },
};

const PLAN_KEY = 'di-agentops-plan-v1';
const CONTEXT_KEY = 'di-agentops-context-v1';

const asArray = (v, fb = []) => Array.isArray(v) ? v : fb;
const asObject = (v, fb = {}) => (v && typeof v === 'object' && !Array.isArray(v)) ? v : fb;
const STATUS_COLORS = { done: '#2e7d32', started: '#8e24aa', 'in-progress': '#ed6c02', blocked: '#d32f2f', pending: '#1565c0', info: '#607d8b' };
const STATUS_TINTS = { done: '#e3eadf', started: '#eee3ea', 'in-progress': '#f1e3d3', blocked: '#efe0d8', pending: '#e8e1d8', info: '#ece8e1' };
const STATUS_BORDERS = { done: '#b7c7aa', started: '#ccb3c8', 'in-progress': '#d8bf9a', blocked: '#d7b3a6', pending: '#c7b8a5', info: '#d2c9bc' };
const SAVED_VIEWS = [
  { id: 'all', label: 'All Updates', filters: {} },
  { id: 'today', label: 'Today', filters: { dateRange: 'today' } },
  { id: 'deploys', label: 'Deploys Today', filters: { dateRange: 'today', deployOnly: true } },
  { id: 'blocked', label: 'Blocked', filters: { status: 'blocked' } },
  { id: '7d', label: 'Last 7 days', filters: { dateRange: '7d' } },
];
const defaultPlan = [
  { id: 'p1', title: 'Capture next 3 priorities', owner: 'Me', status: 'todo', due: '', notes: '' },
  { id: 'p2', title: 'Define work batch for today', owner: 'Me', status: 'in-progress', due: '', notes: '' },
];
const normalizePlan = (items) => {
  const arr = asArray(items, []);
  if (!arr.length) return defaultPlan;
  return arr.map((item, idx) => ({
    id: item?.id || `p-${Date.now()}-${idx}`,
    title: typeof item?.title === 'string' ? item.title : 'Planned work',
    owner: typeof item?.owner === 'string' ? item.owner : 'Me',
    status: typeof item?.status === 'string' ? item.status : 'todo',
    due: typeof item?.due === 'string' ? item.due : '',
    notes: typeof item?.notes === 'string' ? item.notes : '',
  }));
};
const isDone = s => ['done','✅','completed'].includes(s) || (s && s.includes('done'));
const isInProg = s => ['in-progress','in progress'].includes(s);
const isBlocked = s => ['blocked','❌'].includes(s);
const isNotStarted = s => !isDone(s) && !isInProg(s) && !isBlocked(s) && s !== 'info';

const isToday = (ts) => { if (!ts) return false; return new Date(ts).toDateString() === new Date().toDateString(); };
const isWithinDays = (ts, days) => { if (!ts) return false; const c = new Date(); c.setDate(c.getDate() - days); return new Date(ts) >= c; };
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// Brand name normalization mapping
const BRAND_NORMALIZE_MAP = {
  'lec': 'Ladies Entrepreneurship Club',
  'lec magazine': 'Ladies Entrepreneurship Club',
  'lecmagazine': 'Ladies Entrepreneurship Club', 
  'ladies entrepreneurship club': 'Ladies Entrepreneurship Club',
  'ladiesentrepreneurshipclub': 'Ladies Entrepreneurship Club',
  'ladiesentrepreneurshipclub com': 'Ladies Entrepreneurship Club',
  'ladiesentrepreneurshipclub.com': 'Ladies Entrepreneurship Club',
  'apples & pears': 'Apples & Pears',
  'apples and pears': 'Apples & Pears',
  'applesandpears': 'Apples & Pears',
  'applesandpears biz': 'Apples & Pears',
  'applesandpears.biz': 'Apples & Pears',
  'a&p': 'Apples & Pears',
  'aph': 'Apples & Pears',
  'tonisha tagoe': 'Tonisha Tagoe',
  'tonishatagoe': 'Tonisha Tagoe',
  'tonishatagoe com': 'Tonisha Tagoe',
  'tonishatagoe.com': 'Tonisha Tagoe',
  'tt': 'Tonisha Tagoe',
};

const normalizeBrandName = (name) => {
  if (!name) return name;
  const normalized = BRAND_NORMALIZE_MAP[name.toLowerCase().trim()];
  return normalized || name;
};

const WORKSTREAM_STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'into', 'onto', 'your', 'this', 'that', 'these', 'those', 'work', 'task', 'tasks',
  'update', 'updates', 'session', 'started', 'start', 'starting', 'review', 'reviewing', 'fix', 'fixed', 'fixing', 'correct',
  'corrected', 'correction', 'refine', 'refined', 'refinement', 'batch', 'status', 'notes', 'note', 'deploy', 'deployed',
  'deployment', 'live', 'current', 'currently', 'active', 'progress', 'done', 'todo', 'blocked', 'pending', 'option', 'options',
]);

const MARKDOWN_COMPONENTS = {
  h1: ({ ...props }) => <Typography variant="h6" sx={{ mb: 1, mt: 1.25, fontWeight: 700 }} {...props} />,
  h2: ({ ...props }) => <Typography variant="subtitle1" sx={{ mb: 1, mt: 1.25, fontWeight: 700 }} {...props} />,
  h3: ({ ...props }) => <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 700 }} {...props} />,
  p: ({ ...props }) => <Typography variant="body2" sx={{ mb: 1.25, lineHeight: 1.7, overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }} {...props} />,
  ul: ({ ...props }) => <Box component="ul" sx={{ pl: 2.5, my: 1, '& li:last-child': { mb: 0 } }} {...props} />,
  ol: ({ ...props }) => <Box component="ol" sx={{ pl: 2.5, my: 1, '& li:last-child': { mb: 0 } }} {...props} />,
  li: ({ ...props }) => <Box component="li" sx={{ mb: 0.5, lineHeight: 1.7, overflowWrap: 'anywhere', wordBreak: 'break-word' }} {...props} />,
  a: ({ ...props }) => <Box component="a" sx={{ color: 'primary.main', textDecoration: 'underline', wordBreak: 'break-word', overflowWrap: 'anywhere' }} target="_blank" rel="noopener noreferrer" {...props} />,
  strong: ({ ...props }) => <Box component="strong" sx={{ fontWeight: 700 }} {...props} />,
  code: ({ inline, children, ...props }) => inline
    ? <Box component="code" sx={{ px: 0.4, py: 0.15, borderRadius: 0.5, bgcolor: 'rgba(0,0,0,0.06)', fontFamily: 'monospace', fontSize: '0.85em', overflowWrap: 'anywhere', wordBreak: 'break-word' }} {...props}>{children}</Box>
    : <Box component="pre" sx={{ p: 1.25, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.06)', overflowX: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '0.8rem' }}><code {...props}>{children}</code></Box>,
};

const normalizeTaskStatus = (status) => {
  const value = String(status || 'info').trim().toLowerCase();
  if (value === 'todo' || value === 'to do' || value === 'not-started' || value === 'not started') return 'pending';
  if (value === 'in progress') return 'in-progress';
  return value;
};

const prettifyModelName = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^gpt-/i.test(raw)) return `GPT-${raw.replace(/^gpt-/i, '')}`;
  if (/^claude-/i.test(raw)) return raw.replace(/^claude-/i, 'Claude ').replace(/-/g, ' ');
  if (/^gemini-/i.test(raw)) return raw.replace(/^gemini-/i, 'Gemini ').replace(/-/g, ' ');
  return raw;
};

const normalizeMetricCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const collectHistoryModels = (items = []) => [...new Set(items.map(item => prettifyModelName(item?.model)).filter(Boolean))];

const summarizeModels = (models = []) => {
  if (!Array.isArray(models) || models.length === 0) return '';
  if (models.length === 1) return models[0];
  if (models.length === 2) return `${models[0]} + ${models[1]}`;
  return `${models[0]} + ${models.length - 1} more`;
};

const formatCorrectionLabel = (count) => `${count} correction${count === 1 ? '' : 's'}`;

const getHistoryCorrectionCount = (items = []) => {
  const countsByNotification = new Map();
  let standaloneTotal = 0;

  items.forEach((item) => {
    const count = normalizeMetricCount(item?.correction_count);
    if (!count) return;
    const notificationId = String(item?._raw?.notification_id || item?.notification_id || '').trim();
    if (notificationId) {
      countsByNotification.set(notificationId, Math.max(countsByNotification.get(notificationId) || 0, count));
      return;
    }
    standaloneTotal += count;
  });

  return standaloneTotal + [...countsByNotification.values()].reduce((sum, value) => sum + value, 0);
};

const TASK_SIGNAL_STYLES = {
  success: { bg: '#e3eadf', border: '#b7c7aa', color: '#315a31' },
  warning: { bg: '#f1e3d3', border: '#d8bf9a', color: '#825a23' },
  neutral: { bg: '#ece8e1', border: '#d2c9bc', color: '#5e564b' },
  error: { bg: '#efe0d8', border: '#d7b3a6', color: '#8a3b2f' },
};

const toGoalRow = (row, index, prefix = 'row') => {
  const rawProject = String(row?.project || 'Unknown');
  const aliasedProject = PROJECT_ALIASES[rawProject] || rawProject;
  return {
    id: row?.id || `${prefix}-${index}`,
    timestamp: row?.timestamp || '',
    project: normalizeBrandName(aliasedProject) || aliasedProject || 'Unknown',
    raw_project: rawProject,
    canonical_project: aliasedProject,
    agent_ref: row?.agent_ref || (row?.source === 'aph-master-task-list' ? 'ledger' : 'unknown'),
    model: prettifyModelName(row?.model),
    correction_count: normalizeMetricCount(row?.correction_count),
    reply_count: normalizeMetricCount(row?.reply_count),
    agent_reply_count: normalizeMetricCount(row?.agent_reply_count),
    title: row?.title || 'Work update',
    status: normalizeTaskStatus(row?.status),
    source: row?.source || 'unknown',
    deploy_related: !!row?.deploy_related,
    summary: row?.summary || '',
    _raw: row,
  };
};

const buildUpdateTextParts = (rowLike) => {
  const raw = rowLike?._raw || rowLike || {};
  const parts = [];
  if (rowLike?.title) parts.push(`### ${rowLike.title}`);
  if (rowLike?.summary && rowLike.summary !== rowLike.title) parts.push(rowLike.summary);
  if (Array.isArray(raw.notes)) {
    raw.notes.forEach((note) => {
      if (typeof note === 'string') {
        if (note.trim()) parts.push(note.trim());
        return;
      }
      if (note?.title || note?.body) parts.push([note?.title ? `**${note.title}**` : '', note?.body || ''].filter(Boolean).join('\n'));
    });
  }
  if (Array.isArray(raw.changelogs)) {
    raw.changelogs.forEach((change) => {
      if (typeof change === 'string') {
        if (change.trim()) parts.push(change.trim());
        return;
      }
      if (change?.app || change?.summary) parts.push(`- ${[change.app, change.summary].filter(Boolean).join(' - ')}`);
    });
  }
  if (Array.isArray(raw.tasks)) {
    raw.tasks.forEach((task) => {
      if (typeof task === 'string') {
        if (task.trim()) parts.push(`- ${task.trim()}`);
        return;
      }
      if (task?.title) parts.push(`- ${task.title}${task?.status ? ` [${task.status}]` : ''}`);
    });
  }
  return parts.filter(Boolean);
};

const buildUpdateText = (rowLike) => buildUpdateTextParts(rowLike).join('\n\n').trim();

const extractUrlsFromText = (text) => {
  const urlRegex = /(https?:\/\/[^\s<>"{}\\^`\[\]]*)/g;
  return [...new Set(String(text || '').match(urlRegex) || [])];
};

const normalizePathToken = (value) => String(value || '').replace(/[),.;]+$/, '').trim();

const extractFilePathsFromText = (text) => {
  const pathRegex = /(?:\/Users\/ft\/CascadeProjects\/|\/root\/|\/var\/www\/di\/)[^\s<>"'`]+/g;
  const matches = String(text || '').match(pathRegex) || [];
  return [...new Set(matches.map(normalizePathToken).filter(Boolean))];
};

const tokenizeWorkstreamText = (rowLike) => buildUpdateText(rowLike)
  .toLowerCase()
  .replace(/https?:\/\/\S+/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .split(/\s+/)
  .filter(token => token.length >= 3 && !WORKSTREAM_STOPWORDS.has(token));

const normalizeTaskTitle = (title) => String(title || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const normalizeWorkstreamTitle = (item) => {
  if (!item) return WORK_ITEM_GROUP_FALLBACK;
  const raw = item?._raw || {};
  const rawTitle = String(item.title || '').trim();
  const sessionScopedTitle = rawTitle
    .replace(/^(?:session started|new session continued|clean state continued)\s*[:\-]\s*/i, '')
    .replace(/^(?:started|start|starting|continued|continue)\s+/i, '')
    .replace(/\s+build$/i, '')
    .trim();

  const firstTaskTitle = Array.isArray(raw.tasks)
    ? raw.tasks.find(task => typeof task === 'object' && task?.title && normalizeTaskTitle(task.title) !== normalizeTaskTitle(rawTitle))?.title || ''
    : '';
  const firstNoteBody = Array.isArray(raw.notes)
    ? raw.notes.find(note => typeof note === 'object' && note?.body)?.body || ''
    : '';
  const noteMatch = firstNoteBody.match(/(?:starting|working on|continuing|resumed)\s+(.+?)(?:\s+in\s+\/|\s+using\s+\/|\.|,|$)/i);
  const semanticTitle = tokenizeWorkstreamText(item).slice(0, 8).join(' ');
  const candidate = sessionScopedTitle || firstTaskTitle || noteMatch?.[1] || semanticTitle;
  return normalizeTaskTitle(candidate) || WORK_ITEM_GROUP_FALLBACK;
};

const WORK_ITEM_GROUP_FALLBACK = 'ungrouped';
const GENERIC_WORK_ITEM_TITLES = new Set([
  'session started',
  'new session',
  'new session continued',
  'clean state continued',
  'status',
  'test',
  'attempted',
  'work update',
  'task update',
]);

const getWorkItemContextKey = (item) => {
  if (!item) return WORK_ITEM_GROUP_FALLBACK;
  const raw = item?._raw || {};
  const summaryLine = String(item.summary || '').split('\n')[0] || '';
  const firstNoteTitle = Array.isArray(raw.note_titles) ? raw.note_titles[0] || '' : '';
  const semanticKey = tokenizeWorkstreamText(item).slice(0, 8).join(' ');
  const projectKey = normalizeTaskTitle(item.raw_project || item.project || '');
  return normalizeTaskTitle([summaryLine, firstNoteTitle, semanticKey, projectKey].filter(Boolean).join(' ')) || WORK_ITEM_GROUP_FALLBACK;
};

const getWorkItemGroupKey = (item) => {
  if (!item) return WORK_ITEM_GROUP_FALLBACK;
  const brandKey = normalizeBrandName(item.references?.brand || item.project || item.raw_project || 'Unknown');
  const rawTitleKey = normalizeTaskTitle(item.title);
  const titleKey = normalizeWorkstreamTitle(item);
  const needsContext = GENERIC_WORK_ITEM_TITLES.has(rawTitleKey) || titleKey.length < 24;
  const contextKey = needsContext ? getWorkItemContextKey(item) : '';
  return [brandKey, titleKey, contextKey].join('::');
};

const APP_TITLE_HINTS = [
  { app: 'Coda', patterns: [/\bcoda\b/i, /\/var\/www\/di\/data\/coda\//i, /\/api\/coda\b/i, /coda browser/i, /founder and attendee profile enrichment/i, /profile intelligence/i] },
  { app: 'Data Insights', patterns: [/data insights/i, /agentops/i, /google sheets/i] },
  { app: 'Oasis Mail', patterns: [/oasis mail/i, /\boasis\b/i, /isha/i] },
  { app: 'Domino', patterns: [/\bdomino\b/i, /campaign management/i] },
  { app: 'AI Excellence Hub', patterns: [/ai excellence hub/i, /academy/i] },
];

const detectAppFromText = (text) => {
  const value = String(text || '').trim();
  if (!value) return '';
  const match = APP_TITLE_HINTS.find(({ patterns }) => patterns.some(pattern => pattern.test(value)));
  return match?.app || '';
};

function ArtifactList({ urls = [], filePaths = [] }) {
  if (urls.length === 0 && filePaths.length === 0) return null;
  return (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      <Typography variant="caption" fontWeight={600}>Links & Artifacts</Typography>
      {urls.map((url, index) => (
        <Button
          key={`${url}-${index}`}
          size="small"
          href={url}
          target="_blank"
          rel="noopener"
          startIcon={<OpenInNewIcon />}
          sx={{ justifyContent: 'flex-start', textTransform: 'none', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }}
        >
          {url}
        </Button>
      ))}
      {filePaths.map((filePath, index) => (
        <Box key={`${filePath}-${index}`} sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.35 }}>File</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {filePath}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

const BRAND_TITLE_HINTS = [
  { brand: 'Ladies Entrepreneurship Club', patterns: [/\blec\b/i, /ladiesentrepreneurshipclub(?:\.com|\s+com)?/i, /ladies entrepreneurship club/i] },
  { brand: 'Domino', patterns: [/\bdomino\b/i] },
  { brand: 'Signal or Noise', patterns: [/signal or noise/i, /^signal\b/i] },
  { brand: 'Data Insights', patterns: [/data insights/i, /agentops/i, /agent ops/i, /^di\b/i] },
  { brand: 'AI Excellence Hub', patterns: [/ai excellence hub/i, /^aiex\b/i] },
  { brand: 'Tonisha Tagoe', patterns: [/tonisha tagoe/i, /tonishatagoe(?:\.com|\s+com)?/i, /\btt books\b/i] },
  { brand: 'Apples & Pears', patterns: [/apples\s*&\s*pears/i, /apples and pears/i, /applesandpears(?:\.biz|\s+biz)?/i] },
];

const detectBrandFromText = (text) => {
  const value = String(text || '').trim();
  if (!value) return '';
  const match = BRAND_TITLE_HINTS.find(({ patterns }) => patterns.some(pattern => pattern.test(value)));
  return match?.brand || '';
};

const PLUGIN_CATEGORY_BRAND_MAP = {
  APH: 'Apples & Pears',
  LEC: 'Ladies Entrepreneurship Club',
  TT: 'Tonisha Tagoe',
  Baobab: 'Tonisha Tagoe',
};

const dedupeHistoryEntries = (items) => {
  const byKey = {};
  items.forEach((item) => {
    const key = [normalizeBrandName(item?.references?.brand || item?.project), normalizeTaskTitle(item?.title), item?.timestamp || ''].join('::');
    const candidateTextLength = buildUpdateText(item).length;
    const existing = byKey[key];
    if (!existing || candidateTextLength > buildUpdateText(existing).length) {
      byKey[key] = item;
    }
  });
  return Object.values(byKey).sort((a, b) => {
    const dateDiff = new Date(b?.timestamp || 0) - new Date(a?.timestamp || 0);
    if (dateDiff !== 0) return dateDiff;
    return buildUpdateText(b).length - buildUpdateText(a).length;
  });
};

const deriveDisplayStatus = (task) => {
  const rawStatus = normalizeTaskStatus(task?.status);
  if (rawStatus === 'in-progress' && !isWithinDays(task?.timestamp, 2)) return 'started';
  return rawStatus;
};

const deriveTaskRecommendation = ({ task, models = [], correctionCount = 0 }) => {
  const primaryModel = models[0] || 'This model';
  const displayStatus = deriveDisplayStatus(task);

  if (displayStatus === 'blocked') {
    return { tone: 'error', label: 'Blocked work. Fix the constraint first before judging model fit.' };
  }
  if (correctionCount >= 3) {
    return { tone: 'warning', label: 'High correction load. Tighten the brief or switch model next time.' };
  }
  if (correctionCount === 2) {
    return { tone: 'warning', label: 'Needed repeated steering. Keep this model on supervised runs only.' };
  }
  if (correctionCount === 1) {
    return { tone: 'neutral', label: 'One correction needed. Reasonable fit, but still needs review.' };
  }
  if (models.length > 1) {
    return { tone: 'neutral', label: 'Mixed-model history. Compare outcomes here before standardizing.' };
  }
  if (displayStatus === 'done') {
    return { tone: 'success', label: `${primaryModel} handled this cleanly. Reuse for similar tasks.` };
  }
  if (displayStatus === 'started' || displayStatus === 'in-progress') {
    return { tone: 'neutral', label: 'Still active. Wait for the thread to close before judging the model.' };
  }
  return { tone: 'neutral', label: 'Not enough signal yet to rank this model-task pairing.' };
};

function UpdateMarkdown({ text }) {
  if (!text) return null;
  return (
    <Box sx={{ '& > *:last-child': { mb: 0 } }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {text}
      </ReactMarkdown>
    </Box>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="h3" sx={{ fontWeight: 700, color: color || 'text.primary', lineHeight: 1.1, my: 0.5 }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

function DetailDrawer({ row, onClose, effectiveBrands = {} }) {
  const raw = row?._raw || {};
  const notificationId = raw.notification_id || '';
  const canReply = Boolean(notificationId);
  const [replyHistory, setReplyHistory] = useState([]);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [rowStatus, setRowStatus] = useState(row?.display_status || row?.status || 'info');

  useEffect(() => {
    setRowStatus(row?.display_status || row?.status || 'info');
  }, [row]);

  const relatedUpdates = useMemo(() => {
    const history = Array.isArray(row?._history) && row._history.length > 0 ? row._history : (row ? [row] : []);
    return dedupeHistoryEntries(history);
  }, [row]);

  const fullUpdateText = useMemo(() => buildUpdateText(row), [row]);
  const fullUpdateUrls = useMemo(() => extractUrlsFromText(fullUpdateText), [fullUpdateText]);
  const fullUpdateFiles = useMemo(() => extractFilePathsFromText(fullUpdateText), [fullUpdateText]);

  const loadReplyHistory = useCallback(async () => {
    if (!notificationId) return;
    setReplyLoading(true);
    setReplyError('');
    try {
      const res = await fetch(`${VPS_API}/api/agent-work/notifications/${encodeURIComponent(notificationId)}/replies`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load reply history');
      setReplyHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      setReplyError(error.message || 'Failed to load reply history');
      setReplyHistory([]);
    } finally {
      setReplyLoading(false);
    }
  }, [notificationId]);

  useEffect(() => {
    setReplyDraft('');
    setReplyError('');
    if (!notificationId) {
      setReplyHistory([]);
      return;
    }
    loadReplyHistory();
  }, [notificationId, loadReplyHistory]);

  const sendReply = async () => {
    const message = replyDraft.trim();
    if (!notificationId || !message) return;
    setReplySending(true);
    setReplyError('');
    try {
      const res = await fetch(`${VPS_API}/api/agent-work/notifications/${encodeURIComponent(notificationId)}/reply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to send reply');
      setReplyDraft('');
      await loadReplyHistory();
    } catch (error) {
      setReplyError(error.message || 'Failed to send reply');
    } finally {
      setReplySending(false);
    }
  };

  const sortedReplyHistory = useMemo(
    () => replyHistory.slice().sort((a, b) => String(a?.timestamp || '').localeCompare(String(b?.timestamp || ''))),
    [replyHistory],
  );
  const historyModels = useMemo(() => collectHistoryModels(relatedUpdates), [relatedUpdates]);
  const correctionCount = useMemo(() => getHistoryCorrectionCount(relatedUpdates), [relatedUpdates]);
  const liveCorrectionCount = useMemo(
    () => Math.max(correctionCount, sortedReplyHistory.filter(entry => entry?.from === 'user').length),
    [correctionCount, sortedReplyHistory],
  );
  const performanceSignal = useMemo(
    () => deriveTaskRecommendation({ task: row, models: historyModels, correctionCount: liveCorrectionCount }),
    [row, historyModels, liveCorrectionCount],
  );

  if (!row) return null;

  return (
    <Drawer anchor="right" open={!!row} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>{row.project}</Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Copy payload"><IconButton onClick={() => navigator.clipboard.writeText(JSON.stringify(raw, null, 2))}><ContentCopyIcon /></IconButton></Tooltip>
            <IconButton onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {row.references?.brand && <Chip size="small" label={`Brand: ${row.references.brand}`} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
          {row.references?.app && <Chip size="small" label={`App: ${row.references.app}`} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
          {row.references?.plugin && <Chip size="small" label={`Plugin: ${row.references.plugin}`} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
        </Stack>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.25, lineHeight: 1.35, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          {row.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{fmtDate(row.timestamp)}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
          <Chip size="small" label={row.agent_ref} />
          {historyModels.length > 0 && <Chip size="small" label={`Model: ${summarizeModels(historyModels)}`} variant="outlined" />}
          {liveCorrectionCount > 0 && <Chip size="small" label={formatCorrectionLabel(liveCorrectionCount)} sx={{ bgcolor: '#f3e9df' }} />}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={rowStatus} onChange={e => setRowStatus(e.target.value)} sx={{ height: 32 }}>
              {['done', 'started', 'in-progress', 'blocked', 'pending', 'info'].map(s => (
                <MenuItem key={s} value={s}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[s] || '#ccc' }} />
                    <span>{s}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {row.deploy_related && <Chip size="small" label="Deploy" color="primary" />}
          <Chip size="small" label={row.source} variant="outlined" />
        </Stack>
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            border: '1px solid',
            borderColor: TASK_SIGNAL_STYLES[performanceSignal.tone].border,
            bgcolor: TASK_SIGNAL_STYLES[performanceSignal.tone].bg,
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: TASK_SIGNAL_STYLES[performanceSignal.tone].color, mb: 0.35 }}>
            Card Guidance
          </Typography>
          <Typography variant="body2" sx={{ color: TASK_SIGNAL_STYLES[performanceSignal.tone].color, lineHeight: 1.45 }}>
            {performanceSignal.label}
          </Typography>
        </Box>

        {/* FULL UPDATE / HISTORY */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            {relatedUpdates.length > 1 ? `Update History (${relatedUpdates.length})` : 'Full Update'}
          </Typography>
          <Stack spacing={1.25}>
            {relatedUpdates.map((entry, index) => {
              const entryText = buildUpdateText(entry);
              const entryUrls = extractUrlsFromText(entryText);
              const entryFiles = extractFilePathsFromText(entryText);
              const entryStatus = entry.display_status || deriveDisplayStatus(entry);
              return (
                <Box key={`${entry.id || 'entry'}-${entry.timestamp || index}`} sx={{ p: 1.25, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={entryStatus} sx={{ bgcolor: STATUS_COLORS[entryStatus] || '#607d8b', color: '#fff', fontSize: 10 }} />
                      <Chip size="small" label={entry.agent_ref || row.agent_ref} variant="outlined" />
                      {entry.model && <Chip size="small" label={`Model: ${entry.model}`} variant="outlined" />}
                      {normalizeMetricCount(entry.correction_count) > 0 && <Chip size="small" label={formatCorrectionLabel(normalizeMetricCount(entry.correction_count))} sx={{ bgcolor: '#f3e9df' }} />}
                      <Typography variant="caption" color="text.secondary">{fmtDate(entry.timestamp)}</Typography>
                    </Stack>
                    <Chip size="small" label={entry.source || row.source} variant="outlined" />
                  </Stack>
                  <UpdateMarkdown text={entryText || entry.title || 'No update text available.'} />
                  <ArtifactList urls={entryUrls} filePaths={entryFiles} />
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* GOAL ASSIGNMENT */}
        {row.project && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Assign to Goal</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Select Goal</InputLabel>
              <Select label="Select Goal" defaultValue="">
                <MenuItem value="">None</MenuItem>
                {(() => {
                  const brandNames = Object.keys(effectiveBrands || {});
                  const allGoals = [];
                  brandNames.forEach(b => {
                    (effectiveBrands[b]?.goals || []).forEach((g, i) => {
                      allGoals.push({ brand: b, goal: g, id: `${b}-${i}` });
                    });
                  });
                  return allGoals.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.brand}: {item.goal}
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>
          </Box>
        )}
        {fullUpdateText && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Selected Update Text</Typography>
            <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
              <UpdateMarkdown text={fullUpdateText} />
            </Box>
            <ArtifactList urls={fullUpdateUrls} filePaths={fullUpdateFiles} />
          </Box>
        )}
        {Array.isArray(raw.notes) && raw.notes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Notes ({raw.notes.length})</Typography>
            {raw.notes.map((n, i) => (
              <Box key={i} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                {((typeof n === 'string' ? n : n?.title) || '') && <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{typeof n === 'string' ? n : n.title}</Typography>}
                {typeof n === 'object' && n?.body && <UpdateMarkdown text={n.body} />}
              </Box>
            ))}
          </Box>
        )}
        {Array.isArray(raw.tasks) && raw.tasks.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Tasks ({raw.tasks.length})</Typography>
            {raw.tasks.map((t, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Chip size="small" label={typeof t === 'string' ? 'info' : (t.status || 'info')} sx={{ bgcolor: STATUS_COLORS[typeof t === 'string' ? 'info' : t.status] || '#607d8b', color: '#fff', fontSize: 10, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{typeof t === 'string' ? t : t.title}</Typography>
              </Stack>
            ))}
          </Box>
        )}
        {Array.isArray(raw.changelogs) && raw.changelogs.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Changelogs ({raw.changelogs.length})</Typography>
            {raw.changelogs.map((c, i) => (
              <Box key={i} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600} display="block">{typeof c === 'string' ? c : (c.app ? `${c.app}` : 'Changelog')}</Typography>
                {typeof c === 'object' && c.summary && <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, display: 'block' }}>{c.summary}</Typography>}
              </Box>
            ))}
          </Box>
        )}
        {canReply && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Oasis Conversation</Typography>
              <Button size="small" onClick={loadReplyHistory} disabled={replyLoading || replySending}>Refresh</Button>
            </Stack>
            {replyError && <Alert severity="warning" sx={{ mb: 1.5 }}>{replyError}</Alert>}
            <Box sx={{ mb: 2, maxHeight: 200, overflowY: 'auto', bgcolor: 'grey.50', borderRadius: 1, p: 1.5 }}>
              {replyLoading ? (
                <Typography variant="body2" color="text.secondary">Loading conversation…</Typography>
              ) : sortedReplyHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No replies yet for this work update.</Typography>
              ) : sortedReplyHistory.map((entry) => {
                const isAgent = entry?.from === 'agent';
                return (
                  <Box
                    key={`${entry?.id || 'reply'}-${entry?.timestamp || ''}`}
                    sx={{
                      mb: 1,
                      ml: isAgent ? 0 : 'auto',
                      maxWidth: '88%',
                      bgcolor: isAgent ? 'background.paper' : 'primary.main',
                      color: isAgent ? 'text.primary' : 'primary.contrastText',
                      borderRadius: 1.5,
                      border: isAgent ? '1px solid' : 'none',
                      borderColor: 'divider',
                      p: 1.25,
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.8 }}>
                      {isAgent ? (entry?.agent_name || row.agent_ref || 'Agent') : 'You'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{entry?.message || ''}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.75 }}>
                      {fmtDate(entry?.timestamp)}{!isAgent && entry?.acknowledged ? ' · seen' : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
      {canReply && (
        <Stack sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', gap: 1.5, flexShrink: 0 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Include status update:</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              {['done', 'in-progress', 'blocked', 'pending'].map(s => (
                <Chip
                  key={s}
                  size="small"
                  label={s}
                  onClick={() => setReplyDraft(prev => `[status: ${s}]\n${prev}`)}
                  sx={{ cursor: 'pointer', bgcolor: STATUS_COLORS[s] || '#ccc', color: '#fff', fontSize: '0.7rem' }}
                />
              ))}
            </Stack>
          </Box>
          <TextField
            label="Reply to agent"
            value={replyDraft}
            onChange={(event) => setReplyDraft(event.target.value)}
            placeholder="Send follow-up or task back to Oasis thread"
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            disabled={replySending}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Posts to Oasis thread</Typography>
            <Button variant="contained" size="small" onClick={sendReply} disabled={replySending || !replyDraft.trim()}>
              {replySending ? 'Sending…' : 'Send'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}

function ProjectCard({ project, rows, onViewFeed }) {
  const done = rows.filter(r => r.status === 'done').length;
  const blocked = rows.filter(r => r.status === 'blocked').length;
  const inProg = rows.filter(r => r.status === 'in-progress').length;
  const pct = rows.length > 0 ? Math.round((done / rows.length) * 100) : 0;
  const lastUpdate = rows.reduce((l, r) => !r.timestamp ? l : (!l || new Date(r.timestamp) > new Date(l) ? r.timestamp : l), null);
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{project}</Typography>
          <Typography variant="caption" color="text.secondary">Last: {fmtDate(lastUpdate)}</Typography>
        </Box>
        <Button size="small" variant="text" onClick={onViewFeed}>Feed →</Button>
      </Stack>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip size="small" label={`${rows.length} total`} />
        <Chip size="small" label={`${done} done`} color="success" />
        {inProg > 0 && <Chip size="small" label={`${inProg} in progress`} color="warning" />}
        {blocked > 0 && <Chip size="small" label={`${blocked} blocked`} color="error" />}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: blocked > 0 ? '#d32f2f' : '#2e7d32', borderRadius: 4 } }} />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 36 }}>{pct}%</Typography>
      </Stack>
      <Box sx={{ maxHeight: 100, overflowY: 'auto' }}>
        {rows.slice(0, 4).map(r => (
          <Stack key={r.id} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[r.status] || '#ccc', flexShrink: 0 }} />
            <Typography variant="caption" noWrap sx={{ flex: 1 }}>{r.title}</Typography>
          </Stack>
        ))}
        {rows.length > 4 && <Typography variant="caption" color="text.secondary">+{rows.length - 4} more</Typography>}
      </Box>
    </Paper>
  );
}

export default function AgentOpsPage() {
  const [tab, setTab] = useState(0);
  const [selectedStandUpProject, setSelectedStandUpProject] = useState(null);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [plannerTab, setPlannerTab] = useState(0); // 0=Planned Work, 1=Resume Context, 2=Master Tasks
  const [activeView, setActiveView] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [taskSummary, setTaskSummary] = useState({});
  const [sourceSummary, setSourceSummary] = useState({});
  const [detailRow, setDetailRow] = useState(null);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateRange, setFilterDateRange] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [kanbanTaskQuery, setKanbanTaskQuery] = useState('');
  const [deployOnly, setDeployOnly] = useState(false);
  // Chart-specific filters
  const [chartProject, setChartProject] = useState('');
  const [chartAgent, setChartAgent] = useState('');
  const [chartDateRange, setChartDateRange] = useState('');
  const [chartToggle, setChartToggle] = useState({ trend: true, byProject: true, byAgent: true, byStatus: true, deploys: false });
  // Dashboard filter
  const [dashDateRange, setDashDateRange] = useState('');
  const [dashProject, setDashProject] = useState('');
  const [planItems, setPlanItems] = useState(() => {
    try { const r = localStorage.getItem(PLAN_KEY); return r ? normalizePlan(JSON.parse(r)) : defaultPlan; }
    catch { return defaultPlan; }
  });
  const [resumeContext, setResumeContext] = useState(() => localStorage.getItem(CONTEXT_KEY) || '');
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandFilter, setBrandFilter] = useState('all'); // 'all'|'done'|'in-progress'|'needed'
  const [goalTab, setGoalTab] = useState(0); // kept for compat
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  // planningNav: { section: 'company'|'brand'|'plugin'|'app', name: string|null }
  const [planningNav, setPlanningNav] = useState({ section: 'company', name: null });
  const [brandKanbanPlan, setBrandKanbanPlan] = useState(() => {
    try { return JSON.parse(localStorage.getItem('di-brand-kanban-plan') || '{}'); } catch { return {}; }
  });
  const [planInput, setPlanInput] = useState('');
  // Custom brands + plugins (persisted to localStorage, merged with static at render time)
  const [customBrands, setCustomBrands] = useState(() => { try { return JSON.parse(localStorage.getItem('di-custom-brands') || '{}'); } catch { return {}; } });
  const [customPlugins, setCustomPlugins] = useState(() => { try { return JSON.parse(localStorage.getItem('di-custom-plugins') || '{}'); } catch { return {}; } });
  // "Add" dialogs
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const [addPluginOpen, setAddPluginOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', short: '', color: '#1565c0', url: '', desc: '', tech: '', category: 'APH' });
  // Brand sub-sections: keyed by brand name → { products, trainings, comms } each an array of { id, name, status, notes }
  const [brandSections, setBrandSections] = useState(() => { try { return JSON.parse(localStorage.getItem('di-brand-sections') || '{}'); } catch { return {}; } });
  const [brandSectionTab, setBrandSectionTab] = useState(0); // 0=Kanban 1=Products 2=Training 3=Comms
  // Kanban display mode: 'all' = every update, 'latest' = deduped by title (latest status per task)
  const [kanbanMode, setKanbanMode] = useState('latest');
  const [sectionInputs, setSectionInputs] = useState({ products: '', trainings: '', comms: '' });

  const loadUpdates = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${VPS_API}/api/agent-work/updates`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setRows(asArray(data.updates));
      setTaskSummary(asObject(data.taskSummary));
      setSourceSummary(asObject(data.sources));
    } catch (e) { setError(e.message); setRows([]); }
    finally { setLoading(false); }
  };

  // Helpers for persisted custom data
  const saveCustomBrand = (name, meta) => {
    const next = { ...customBrands, [name]: meta };
    setCustomBrands(next);
    localStorage.setItem('di-custom-brands', JSON.stringify(next));
  };
  const saveCustomPlugin = (name, meta) => {
    const next = { ...customPlugins, [name]: meta };
    setCustomPlugins(next);
    localStorage.setItem('di-custom-plugins', JSON.stringify(next));
  };
  const saveBrandSections = (brandName, sections) => {
    const next = { ...brandSections, [brandName]: sections };
    setBrandSections(next);
    localStorage.setItem('di-brand-sections', JSON.stringify(next));
  };

  // Effective maps = static + custom
  const effectiveBrands = { ...PORTFOLIO_BRANDS, ...customBrands };
  const effectivePlugins = { ...PLUGIN_META, ...customPlugins };
  const brandAliasLookup = useMemo(() => {
    const lookup = {};
    Object.entries(effectiveBrands).forEach(([brand, meta]) => {
      lookup[brand.toLowerCase()] = brand;
      (meta.aliases || []).forEach(alias => {
        lookup[String(alias || '').toLowerCase()] = brand;
      });
    });
    return lookup;
  }, [effectiveBrands]);

  const decorateGoalRow = useCallback((row) => {
    const canonicalProject = row.canonical_project || PROJECT_ALIASES[row.raw_project] || row.raw_project || row.project || 'Unknown';
    const referencePlugin = effectivePlugins[canonicalProject] ? canonicalProject : '';
    const explicitApp = detectAppFromText([row.title, row.summary, buildUpdateText(row), row.raw_project, canonicalProject].filter(Boolean).join('\n'));
    const referenceApp = explicitApp || (APP_META[canonicalProject] ? canonicalProject : '');
    const normalizedProjectBrand = normalizeBrandName(canonicalProject);
    const projectBrand = brandAliasLookup[String(canonicalProject).toLowerCase()] || brandAliasLookup[String(row.project || '').toLowerCase()] || brandAliasLookup[String(row.raw_project || '').toLowerCase()] || (normalizedProjectBrand !== canonicalProject ? normalizedProjectBrand : '');
    const titleBrand = detectBrandFromText(row.title);
    const summaryBrand = titleBrand ? '' : detectBrandFromText(String(row.summary || '').split('\n')[0]);
    const pluginBrand = referencePlugin ? (PLUGIN_CATEGORY_BRAND_MAP[effectivePlugins[referencePlugin]?.category] || '') : '';
    const appBrand = effectiveBrands[referenceApp] ? referenceApp : '';
    const referenceBrand = titleBrand || summaryBrand || projectBrand || pluginBrand || appBrand;
    return {
      ...row,
      references: {
        brand: referenceBrand,
        plugin: referencePlugin,
        app: referenceApp,
      },
    };
  }, [brandAliasLookup, effectivePlugins]);

  useEffect(() => { loadUpdates(); }, []);
  useEffect(() => { localStorage.setItem(PLAN_KEY, JSON.stringify(planItems)); }, [planItems]);
    useEffect(() => { localStorage.setItem('di-brand-kanban-plan', JSON.stringify(brandKanbanPlan)); }, [brandKanbanPlan]);
  useEffect(() => { localStorage.setItem(CONTEXT_KEY, resumeContext); }, [resumeContext]);

  // APH Master Task List entries go to Goals tab, not the work feed
  const normalizedRows = useMemo(() => rows.filter(r => r.source !== 'aph-master-task-list').map((r, i) => toGoalRow(r, i, 'row')), [rows]);
  const aphMasterRows = useMemo(() => rows.filter(r => r.source === 'aph-master-task-list').map((r, i) => toGoalRow(r, i, 'aph')), [rows]);
  const companyGoalRows = useMemo(() => [...normalizedRows, ...aphMasterRows].map(decorateGoalRow), [normalizedRows, aphMasterRows, decorateGoalRow]);

  // APH Company Goals — grouped by project section
  const aphGoalRows = useMemo(() => {
    const entries = aphMasterRows;
    const grouped = {};
    entries.forEach(r => { if (!grouped[r.project]) grouped[r.project] = []; grouped[r.project].push(r); });
    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
  }, [aphMasterRows]);

  // Group Company Goals rows by canonical plugin/app/brand references
  const pluginGroupedRows = useMemo(() => {
    const groups = {};
    companyGoalRows.forEach(r => {
      const plugin = r.references?.plugin;
      if (!plugin) return;
      if (!groups[plugin]) groups[plugin] = [];
      groups[plugin].push(r);
    });
    return groups;
  }, [companyGoalRows]);

  const pluginNavigationGroups = useMemo(() => {
    const grouped = {};
    Object.entries(effectivePlugins)
      .map(([name, meta]) => ({
        name,
        meta,
        count: (pluginGroupedRows[name] || []).length,
      }))
      .sort((a, b) => {
        const categoryA = PLUGIN_CATEGORY_ORDER.indexOf(a.meta.category);
        const categoryB = PLUGIN_CATEGORY_ORDER.indexOf(b.meta.category);
        const orderA = categoryA === -1 ? Number.MAX_SAFE_INTEGER : categoryA;
        const orderB = categoryB === -1 ? Number.MAX_SAFE_INTEGER : categoryB;
        if (orderA !== orderB) return orderA - orderB;
        if (b.count !== a.count) return b.count - a.count;
        return a.name.localeCompare(b.name);
      })
      .forEach(entry => {
        const category = entry.meta.category || 'Other';
        if (!grouped[category]) grouped[category] = [];
        grouped[category].push(entry);
      });
    return Object.entries(grouped);
  }, [effectivePlugins, pluginGroupedRows]);

  const appGroupedRows = useMemo(() => {
    const groups = {};
    companyGoalRows.forEach(r => {
      const appName = r.references?.app;
      if (!appName) return;
      if (!groups[appName]) groups[appName] = [];
      groups[appName].push(r);
    });
    return groups;
  }, [companyGoalRows]);

  const portfolioBrandRows = useMemo(() => {
    const groups = {};
    Object.keys(effectiveBrands).forEach(b => { groups[b] = []; });
    companyGoalRows.forEach(r => {
      const brand = r.references?.brand;
      if (brand && groups[brand]) groups[brand].push(r);
    });
    return groups;
  }, [companyGoalRows, effectiveBrands]);

  const applyView = useCallback((viewId) => {
    setActiveView(viewId);
    const v = SAVED_VIEWS.find(sv => sv.id === viewId);
    if (!v) return;
    const f = v.filters;
    setFilterDateRange(f.dateRange || '');
    setDeployOnly(!!f.deployOnly);
    setFilterStatus(f.status || '');
    setFilterProject(f.project || '');
    setFilterAgent('');
    setSearch('');
  }, []);

  const projects = useMemo(() => [...new Set(normalizedRows.map(r => r.project))].sort(), [normalizedRows]);
  const agents = useMemo(() => [...new Set(normalizedRows.map(r => r.agent_ref))].sort(), [normalizedRows]);

  const filteredRows = useMemo(() => normalizedRows.filter(r => {
    if (search) { const q = search.toLowerCase(); if (![r.project, r.agent_ref, r.title, r.status, r.source, r.summary].join(' ').toLowerCase().includes(q)) return false; }
    if (filterProject && r.project !== filterProject) return false;
    if (filterAgent && r.agent_ref !== filterAgent) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    if (deployOnly && !r.deploy_related) return false;
    if (filterDateRange === 'today' && !isToday(r.timestamp)) return false;
    if (filterDateRange === '7d' && !isWithinDays(r.timestamp, 7)) return false;
    if (filterDateRange === '30d' && !isWithinDays(r.timestamp, 30)) return false;
    if (filterDateRange === 'before-2d' && isWithinDays(r.timestamp, 2)) return false;
    if (filterDateFrom && r.timestamp && new Date(r.timestamp) < new Date(filterDateFrom + 'T00:00:00')) return false;
    if (filterDateTo && r.timestamp && new Date(r.timestamp) > new Date(filterDateTo + 'T23:59:59')) return false;
    return true;
  }), [normalizedRows, search, filterProject, filterAgent, filterStatus, deployOnly, filterDateRange, filterDateFrom, filterDateTo]);

  const kpis = useMemo(() => ({
    total: normalizedRows.length,
    done: normalizedRows.filter(r => r.status === 'done').length,
    blocked: normalizedRows.filter(r => r.status === 'blocked').length,
    inProgress: normalizedRows.filter(r => r.status === 'in-progress').length,
    deploys: normalizedRows.filter(r => r.deploy_related).length,
    todayCount: normalizedRows.filter(r => isToday(r.timestamp)).length,
  }), [normalizedRows]);

  const filteredKpis = useMemo(() => ({
    total: filteredRows.length,
    done: filteredRows.filter(r => r.status === 'done').length,
    blocked: filteredRows.filter(r => r.status === 'blocked').length,
    inProgress: filteredRows.filter(r => r.status === 'in-progress').length,
    deploys: filteredRows.filter(r => r.deploy_related).length,
    todayCount: filteredRows.filter(r => isToday(r.timestamp)).length,
    isFiltered: !!(search || filterProject || filterAgent || filterStatus || filterDateRange || filterDateFrom || filterDateTo || deployOnly),
  }), [filteredRows, search, filterProject, filterAgent, filterStatus, filterDateRange, filterDateFrom, filterDateTo, deployOnly]);

  const dashRows = useMemo(() => {
    let r = normalizedRows;
    if (dashProject) r = r.filter(x => x.project === dashProject);
    if (dashDateRange === 'today') r = r.filter(x => isToday(x.timestamp));
    if (dashDateRange === '7d') r = r.filter(x => isWithinDays(x.timestamp, 7));
    if (dashDateRange === '30d') r = r.filter(x => isWithinDays(x.timestamp, 30));
    return r;
  }, [normalizedRows, dashProject, dashDateRange]);

  const dashKpis = useMemo(() => ({
    total: dashRows.length,
    done: dashRows.filter(r => r.status === 'done').length,
    blocked: dashRows.filter(r => r.status === 'blocked').length,
    inProgress: dashRows.filter(r => r.status === 'in-progress').length,
    deploys: dashRows.filter(r => r.deploy_related).length,
    todayCount: dashRows.filter(r => isToday(r.timestamp)).length,
  }), [dashRows]);

  const chartRows = useMemo(() => {
    let r = normalizedRows;
    if (chartProject) r = r.filter(x => x.project === chartProject);
    if (chartAgent) r = r.filter(x => x.agent_ref === chartAgent);
    if (chartDateRange === 'today') r = r.filter(x => isToday(x.timestamp));
    if (chartDateRange === '7d') r = r.filter(x => isWithinDays(x.timestamp, 7));
    if (chartDateRange === '30d') r = r.filter(x => isWithinDays(x.timestamp, 30));
    return r;
  }, [normalizedRows, chartProject, chartAgent, chartDateRange]);

  const trendData = useMemo(() => {
    const bds = { done: {}, 'in-progress': {}, blocked: {} };
    chartRows.forEach(r => {
      if (!r.timestamp) return;
      const d = new Date(r.timestamp); if (isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 10);
      if (bds[r.status]) bds[r.status][key] = (bds[r.status][key] || 0) + 1;
    });
    const labels = [...new Set(Object.values(bds).flatMap(m => Object.keys(m)))].sort().slice(-60);
    return {
      labels,
      datasets: [
        { label: 'Done', data: labels.map(l => bds.done[l] || 0), borderColor: '#2e7d32', tension: 0.3, fill: false },
        { label: 'In Progress', data: labels.map(l => bds['in-progress'][l] || 0), borderColor: '#ed6c02', tension: 0.3, fill: false },
        { label: 'Blocked', data: labels.map(l => bds.blocked[l] || 0), borderColor: '#d32f2f', tension: 0.3, fill: false },
      ],
    };
  }, [chartRows]);

  const projectStatusData = useMemo(() => {
    const pm = {};
    chartRows.forEach(r => {
      if (!pm[r.project]) pm[r.project] = { done: 0, 'in-progress': 0, blocked: 0 };
      const b = ['done', 'in-progress', 'blocked'].includes(r.status) ? r.status : 'done';
      pm[r.project][b]++;
    });
    const labels = Object.keys(pm).sort((a, b) => (pm[b].done + pm[b]['in-progress'] + pm[b].blocked) - (pm[a].done + pm[a]['in-progress'] + pm[a].blocked));
    return {
      labels,
      datasets: [
        { label: 'Done', data: labels.map(l => pm[l].done), backgroundColor: '#2e7d32' },
        { label: 'In Progress', data: labels.map(l => pm[l]['in-progress']), backgroundColor: '#ed6c02' },
        { label: 'Blocked', data: labels.map(l => pm[l].blocked), backgroundColor: '#d32f2f' },
      ],
    };
  }, [chartRows]);

  const agentDonutData = useMemo(() => {
    const am = {};
    chartRows.forEach(r => { am[r.agent_ref] = (am[r.agent_ref] || 0) + 1; });
    const labels = Object.keys(am).sort((a, b) => am[b] - am[a]);
    const palette = ['#002B45', '#1565c0', '#2e7d32', '#ed6c02', '#d32f2f', '#607d8b', '#7b1fa2'];
    return { labels, datasets: [{ data: labels.map(l => am[l]), backgroundColor: labels.map((_, i) => palette[i % palette.length]) }] };
  }, [chartRows]);

  const statusDonutData = useMemo(() => {
    const sm = { done: 0, 'in-progress': 0, blocked: 0, other: 0 };
    chartRows.forEach(r => { if (sm[r.status] !== undefined) sm[r.status]++; else sm.other++; });
    return { labels: ['Done', 'In Progress', 'Blocked', 'Other'], datasets: [{ data: [sm.done, sm['in-progress'], sm.blocked, sm.other], backgroundColor: ['#2e7d32','#ed6c02','#d32f2f','#607d8b'] }] };
  }, [chartRows]);

  const deployTrendData = useMemo(() => {
    const dm = {};
    chartRows.filter(r => r.deploy_related).forEach(r => {
      if (!r.timestamp) return;
      const d = new Date(r.timestamp); if (isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 10);
      dm[key] = (dm[key] || 0) + 1;
    });
    const labels = Object.keys(dm).sort().slice(-60);
    return { labels, datasets: [{ label: 'Deploys', data: labels.map(l => dm[l] || 0), borderColor: '#002B45', backgroundColor: 'rgba(0,43,69,0.1)', tension: 0.3, fill: true }] };
  }, [chartRows]);

  const projectRows = useMemo(() => {
    const map = {};
    normalizedRows.forEach(r => { if (!map[r.project]) map[r.project] = []; map[r.project].push(r); });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [normalizedRows]);

  const columns = [
    { field: 'timestamp', headerName: 'Timestamp', flex: 1, minWidth: 150, renderCell: p => <Typography variant="caption">{fmtDate(p.value)}</Typography> },
    { field: 'project', headerName: 'Project', flex: 0.8, minWidth: 120 },
    { field: 'agent_ref', headerName: 'Agent', flex: 0.6, minWidth: 90 },
    { field: 'title', headerName: 'Task / Update', flex: 2, minWidth: 240 },
    {
      field: 'status', headerName: 'Status', flex: 0.6, minWidth: 110,
      renderCell: p => { const v = String(p.value || ''); const c = v === 'done' ? 'success' : v === 'blocked' ? 'error' : v === 'in-progress' ? 'warning' : 'default'; return <Chip size="small" label={v || 'info'} color={c} />; }
    },
    { field: 'deploy_related', headerName: 'Deploy?', flex: 0.4, minWidth: 80, renderCell: p => p.value ? <Chip size="small" label="Yes" color="primary" /> : null },
    {
      field: 'source', headerName: 'Source', flex: 0.8, minWidth: 130,
      renderCell: p => { const v = String(p.value || ''); const c = v === 'aph-master-task-list' ? 'secondary' : v.startsWith('notification') ? 'primary' : 'default'; return <Chip size="small" label={v} color={c} variant="outlined" />; }
    },
  ];

  const clearFilters = () => { setSearch(''); setFilterProject(''); setFilterAgent(''); setFilterStatus(''); setFilterDateRange(''); setFilterDateFrom(''); setFilterDateTo(''); setDeployOnly(false); setActiveView('all'); };
  const hasFilters = !!(search || filterProject || filterAgent || filterStatus || filterDateRange || filterDateFrom || filterDateTo || deployOnly);
  const updatePlan = (id, key, val) => setPlanItems(prev => prev.map(p => p.id === id ? { ...p, [key]: val } : p));

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: 6 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>AgentOps</Typography>
          <Typography variant="body2" color="text.secondary">{normalizedRows.length} records · {kpis.todayCount} today</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<AddTaskIcon />} size="small" variant="outlined" onClick={() => { setPlannerTab(0); setPlannerOpen(true); }}>Planner</Button>
          <Button startIcon={<BookmarkIcon />} size="small" variant="outlined" onClick={() => { setPlannerTab(2); setPlannerOpen(true); }}>Master Tasks</Button>
          <Button startIcon={<RefreshIcon />} variant="contained" onClick={loadUpdates} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Overview" />
        <Tab label="Company Goals" />
        <Tab label="Stand Up" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {/* Dashboard filter bar */}
          <Paper sx={{ p: 1.5, mb: 2.5, bgcolor: 'grey.50' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
              <FilterListIcon color="action" sx={{ fontSize: 18 }} />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Project</InputLabel>
                <Select value={dashProject} label="Project" onChange={e => setDashProject(e.target.value)}>
                  <MenuItem value="">All projects</MenuItem>
                  {projects.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Date Range</InputLabel>
                <Select value={dashDateRange} label="Date Range" onChange={e => setDashDateRange(e.target.value)}>
                  <MenuItem value="">All time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                </Select>
              </FormControl>
              {(dashProject || dashDateRange) && (
                <Chip size="small" label="Clear" onClick={() => { setDashProject(''); setDashDateRange(''); }} onDelete={() => { setDashProject(''); setDashDateRange(''); }} />
              )}
              {(dashProject || dashDateRange) && (
                <Typography variant="caption" color="text.secondary">{dashRows.length} of {normalizedRows.length} records</Typography>
              )}
            </Stack>
          </Paper>

          {/* KPI grid */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Updates Today', value: dashKpis.todayCount, color: '#1565c0' },
              { label: `${dashDateRange || 'All'} Total`, value: dashKpis.total },
              { label: 'Done', value: dashKpis.done, color: '#2e7d32' },
              { label: 'In Progress', value: dashKpis.inProgress, color: '#ed6c02' },
              { label: 'Blocked', value: dashKpis.blocked, color: '#d32f2f' },
              { label: 'Deploys', value: dashKpis.deploys, color: '#002B45' },
              { label: 'Oasis Records', value: Number(sourceSummary.oasis_updates || 0) },
              { label: 'APH Goal Tasks', value: Number(sourceSummary.aph_master_task_list || 0) },
              { label: 'Project Logs', value: Number(sourceSummary.project_update_logs || 0) },
              { label: 'ChatGPT', value: Number(sourceSummary.chatgpt_sessions || 0) + Number(sourceSummary.chatgpt_desktop || 0) },
              { label: 'Claude', value: Number(sourceSummary.claude_sessions || 0) + Number(sourceSummary.claude_desktop || 0) },
              { label: 'Kiro', value: Number(sourceSummary.kiro_sessions || 0) },
              { label: 'Copilot/Other', value: Number(sourceSummary.antigravity_sessions || 0) },
            ].map(item => (
              <Grid item xs={6} sm={3} md="auto" key={item.label} sx={{ minWidth: 110 }}>
                <KpiCard label={item.label} value={item.value} color={item.color} />
              </Grid>
            ))}
          </Grid>

          {/* Detailed Charts */}
          <Paper sx={{ p: 1.5, mb: 2.5, bgcolor: 'grey.50' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
              <FilterListIcon color="action" sx={{ fontSize: 18 }} />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Project</InputLabel>
                <Select value={chartProject} label="Project" onChange={e => setChartProject(e.target.value)}>
                  <MenuItem value="">All projects</MenuItem>
                  {projects.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Agent</InputLabel>
                <Select value={chartAgent} label="Agent" onChange={e => setChartAgent(e.target.value)}>
                  <MenuItem value="">All agents</MenuItem>
                  {agents.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Date Range</InputLabel>
                <Select value={chartDateRange} label="Date Range" onChange={e => setChartDateRange(e.target.value)}>
                  <MenuItem value="">All time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                </Select>
              </FormControl>
              {(chartProject || chartAgent || chartDateRange) && (
                <Chip size="small" label="Clear" onClick={() => { setChartProject(''); setChartAgent(''); setChartDateRange(''); }} onDelete={() => { setChartProject(''); setChartAgent(''); setChartDateRange(''); }} />
              )}
              <Typography variant="caption" color="text.secondary">{chartRows.length} records in view</Typography>
              <Box sx={{ ml: 'auto !important' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Show:</Typography>
                {[['trend','Trend'],['byProject','By Project'],['byAgent','By Agent'],['byStatus','By Status'],['deploys','Deploys']].map(([k, lbl]) => (
                  <Chip key={k} size="small" label={lbl} sx={{ mr: 0.5 }}
                    color={chartToggle[k] ? 'primary' : 'default'} variant={chartToggle[k] ? 'filled' : 'outlined'}
                    onClick={() => setChartToggle(t => ({ ...t, [k]: !t[k] }))}
                  />
                ))}
              </Box>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            {chartToggle.trend && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Work Trend (Done / In Progress / Blocked over time)</Typography>
                  <Box sx={{ height: 300 }}>
                    <Line data={trendData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
                  </Box>
                </Paper>
              </Grid>
            )}
            {chartToggle.deploys && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Deploy Frequency</Typography>
                  <Box sx={{ height: 240 }}>
                    <Line data={deployTrendData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
                  </Box>
                </Paper>
              </Grid>
            )}
            {chartToggle.byProject && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Status by Project (stacked · sorted by volume)</Typography>
                  <Box sx={{ height: 340 }}>
                    <Bar data={projectStatusData} options={{ indexAxis: 'y', responsive: true, maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true } }, plugins: { legend: { position: 'bottom' } } }} />
                  </Box>
                </Paper>
              </Grid>
            )}
            {chartToggle.byAgent && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Agent Share</Typography>
                  <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Doughnut data={agentDonutData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />
                  </Box>
                </Paper>
              </Grid>
            )}
            {chartToggle.byStatus && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Status Breakdown</Typography>
                  <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Doughnut data={statusDonutData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {tab === 1 && (() => {
        // Determine scope for filtering
        const scopeLabel = selectedBrand ? 'brand' : selectedPlugin ? 'plugin' : selectedApp ? 'app' : null;
        const scopeTitle = selectedBrand || selectedPlugin || selectedApp || 'Company Overview';
        
        // Get tasks for current selection (or all if Company Overview)
        const scopeTasks = !selectedBrand && !selectedPlugin && !selectedApp
          ? companyGoalRows
          : selectedBrand
            ? (portfolioBrandRows[selectedBrand] || [])
            : selectedPlugin
              ? (pluginGroupedRows[selectedPlugin] || [])
              : (appGroupedRows[selectedApp] || []);

        const filteredScopeTasks = scopeTasks.filter(task => {
          if (filterDateRange === 'today' && !isToday(task.timestamp)) return false;
          if (filterDateRange === '7d' && !isWithinDays(task.timestamp, 7)) return false;
          if (filterDateRange === '30d' && !isWithinDays(task.timestamp, 30)) return false;
          if (filterDateFrom && task.timestamp && new Date(task.timestamp) < new Date(`${filterDateFrom}T00:00:00`)) return false;
          if (filterDateTo && task.timestamp && new Date(task.timestamp) > new Date(`${filterDateTo}T23:59:59`)) return false;
          if (kanbanTaskQuery) {
            const haystack = [task.title, task.summary, buildUpdateText(task), task.project, task.references?.brand, task.references?.plugin, task.references?.app].join(' ').toLowerCase();
            if (!haystack.includes(kanbanTaskQuery.toLowerCase())) return false;
          }
          return true;
        });

        const visibleEntryRows = filteredScopeTasks.filter(task => ['pending', 'started', 'in-progress', 'done', 'blocked'].includes(deriveDisplayStatus(task)));

        const groupedTasks = {};
        filteredScopeTasks
          .slice()
          .sort((a, b) => {
            const dateDiff = new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
            if (dateDiff !== 0) return dateDiff;
            return buildUpdateText(b).length - buildUpdateText(a).length;
          })
          .forEach(task => {
            const groupKey = getWorkItemGroupKey(task);
            if (!groupedTasks[groupKey]) groupedTasks[groupKey] = { items: [] };
            groupedTasks[groupKey].items.push(task);
          });

        const uniqueTasks = Object.values(groupedTasks)
          .map(group => {
            const history = dedupeHistoryEntries(group.items);
            const representative = history.find(item => ['pending', 'in-progress', 'done', 'blocked', 'todo'].includes(String(item.status || '').toLowerCase())) || history[0];
            if (!representative) return null;
            const models = collectHistoryModels(history);
            const correctionCount = getHistoryCorrectionCount(history);
            const recommendation = deriveTaskRecommendation({ task: representative, models, correctionCount });
            return {
              ...representative,
              display_status: deriveDisplayStatus(representative),
              _history: history,
              _historyCount: history.length,
              models,
              correction_count: correctionCount,
              recommendation: recommendation.label,
              recommendation_tone: recommendation.tone,
            };
          })
          .filter(task => task && ['pending', 'started', 'in-progress', 'done', 'blocked'].includes(task.display_status));

        // Calculate stats
        const stats = {
          done: visibleEntryRows.filter(t => deriveDisplayStatus(t) === 'done').length,
          started: visibleEntryRows.filter(t => deriveDisplayStatus(t) === 'started').length,
          inProgress: visibleEntryRows.filter(t => deriveDisplayStatus(t) === 'in-progress').length,
          blocked: visibleEntryRows.filter(t => deriveDisplayStatus(t) === 'blocked').length,
          pending: visibleEntryRows.filter(t => deriveDisplayStatus(t) === 'pending').length,
          total: visibleEntryRows.length
        };

        return (
          <Stack spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
            {/* STATS BLOCK */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f5f5f5', flex: '1 1 120px' }}><Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 700 }}>{stats.done}</Typography><Typography variant="caption">Done entries</Typography></Paper>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f5f5f5', flex: '1 1 120px' }}><Typography variant="h6" sx={{ color: '#8e24aa', fontWeight: 700 }}>{stats.started}</Typography><Typography variant="caption">Started entries</Typography></Paper>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f5f5f5', flex: '1 1 120px' }}><Typography variant="h6" sx={{ color: '#ed6c02', fontWeight: 700 }}>{stats.inProgress}</Typography><Typography variant="caption">In progress entries</Typography></Paper>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f5f5f5', flex: '1 1 120px' }}><Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 700 }}>{stats.blocked}</Typography><Typography variant="caption">Blocked entries</Typography></Paper>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f5f5f5', flex: '1 1 120px' }}><Typography variant="h6" sx={{ color: '#607d8b', fontWeight: 700 }}>{stats.pending}</Typography><Typography variant="caption">To do entries</Typography></Paper>
            </Box>

            {/* MAIN CONTENT - Left (Navigation) + Right (Details) */}
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
              {/* LEFT SIDEBAR - Navigation */}
              <Box sx={{ width: 240, flexShrink: 0, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa', overflowY: 'auto' }}>
                <Stack spacing={0} sx={{ p: 1 }}>
                  {/* Company Overview */}
                  <Box
                    onClick={() => {
                      setSelectedBrand(null);
                      setSelectedPlugin(null);
                      setSelectedApp(null);
                    }}
                    sx={{
                      p: 1.2,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: !selectedBrand && !selectedPlugin && !selectedApp ? 'primary.main' : '#e0e0e0',
                      bgcolor: !selectedBrand && !selectedPlugin && !selectedApp ? '#E3F2FD' : 'background.paper',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'primary.main' },
                      mb: 1
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} sx={{ color: !selectedBrand && !selectedPlugin && !selectedApp ? 'primary.main' : 'text.primary' }}>Company Overview</Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Brands */}
                  <Typography variant="caption" fontWeight={700} sx={{ px: 1, py: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>Brands</Typography>
                  {Object.entries(effectiveBrands).map(([brand, meta]) => (
                    <Box
                      key={brand}
                      onClick={() => {
                        setSelectedBrand(brand);
                        setSelectedPlugin(null);
                        setSelectedApp(null);
                      }}
                      sx={{
                        p: 1.2,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: selectedBrand === brand ? 'primary.main' : '#e0e0e0',
                        bgcolor: selectedBrand === brand ? '#E3F2FD' : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.main' },
                        mb: 0.5
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color }} />
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.85rem' }}>{brand}</Typography>
                      </Stack>
                    </Box>
                  ))}

                  <Divider sx={{ my: 1 }} />

                  {/* Apps */}
                  <Typography variant="caption" fontWeight={700} sx={{ px: 1, py: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>Apps</Typography>
                            {Object.entries(APP_META).map(([app, meta]) => (
                    <Box
                      key={app}
                      onClick={() => {
                        setSelectedApp(app);
                        setSelectedBrand(null);
                        setSelectedPlugin(null);
                      }}
                      sx={{
                        p: 1.2,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: selectedApp === app ? 'primary.main' : '#e0e0e0',
                        bgcolor: selectedApp === app ? '#E3F2FD' : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.main' },
                        mb: 0.5
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color }} />
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.85rem' }}>{app}</Typography>
                      </Stack>
                    </Box>
                  ))}

                  <Divider sx={{ my: 1 }} />

                  {/* Plugins */}
                  <Typography variant="caption" fontWeight={700} sx={{ px: 1, py: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>Plugins</Typography>
                  {pluginNavigationGroups.map(([category, plugins]) => (
                    <Box key={category}>
                      <Typography variant="caption" sx={{ px: 1, py: 0.5, color: 'text.disabled', textTransform: 'uppercase', display: 'block' }}>{category}</Typography>
                      {plugins.map(({ name, meta, count }) => (
                        <Box
                          key={name}
                          onClick={() => {
                            setSelectedPlugin(name);
                            setSelectedBrand(null);
                            setSelectedApp(null);
                          }}
                          sx={{
                            p: 1.2,
                            borderRadius: 1,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: selectedPlugin === name ? 'primary.main' : '#e0e0e0',
                            bgcolor: selectedPlugin === name ? '#E3F2FD' : 'background.paper',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: 'primary.main' },
                            mb: 0.5
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color }} />
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.85rem', flex: 1 }}>{name}</Typography>
                            {count > 0 ? <Chip size="small" label={count} sx={{ height: 20, bgcolor: '#f5efe7' }} /> : null}
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* RIGHT PANEL - Goals + Kanban */}
              <Box sx={{ flex: 1, display: 'flex', gap: 2, minWidth: 0, overflow: 'hidden' }}>
                {/* GOALS - Left vertical */}
                {selectedBrand && effectiveBrands[selectedBrand]?.goals && effectiveBrands[selectedBrand].goals.length > 0 ? (
                  <Box sx={{ width: 240, flexShrink: 0, borderRight: '1px solid #e0e0e0', overflowY: 'auto' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ p: 1.5, pb: 1 }}>Goals</Typography>
                    <Stack spacing={1} sx={{ px: 1, pb: 2 }}>
                      {effectiveBrands[selectedBrand].goals.map((goal, i) => (
                        <Paper key={i} sx={{ p: 1, bgcolor: '#fafafa', borderLeft: `3px solid ${effectiveBrands[selectedBrand].color}`, fontSize: '0.8rem' }}>
                          <Typography variant="caption" sx={{ lineHeight: 1.3 }}>{goal}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                {/* KANBAN - Right */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  {/* Filter bar */}
                  <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight={600}>{scopeTitle}</Typography>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Date Range</InputLabel>
                      <Select value={filterDateRange} label="Date Range" onChange={e => setFilterDateRange(e.target.value)}>
                        <MenuItem value="">All dates</MenuItem>
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="7d">Last 7 days</MenuItem>
                        <MenuItem value="30d">Last 30 days</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} sx={{ minWidth: 145 }} />
                    <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} sx={{ minWidth: 145 }} />
                    <TextField size="small" label="Task filter" value={kanbanTaskQuery} onChange={e => setKanbanTaskQuery(e.target.value)} placeholder="Task, URL, note text" sx={{ minWidth: 220, flex: '1 1 220px' }} />
                    <Chip size="small" label={`${visibleEntryRows.length} entries · ${uniqueTasks.length} cards`} variant="outlined" />
                    <Button size="small" onClick={() => { setFilterDateRange(''); setFilterDateFrom(''); setFilterDateTo(''); setKanbanTaskQuery(''); }}>Clear</Button>
                  </Paper>

                  {/* Kanban Columns */}
                  <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 2, flex: 1 }}>
                    {['pending', 'started', 'in-progress', 'done', 'blocked'].map(status => {
                      const statusLabel = status === 'pending' ? 'To Do' : status === 'started' ? 'Started' : status === 'in-progress' ? 'In Progress' : status === 'done' ? 'Done' : 'Blocked';
                      const statusColor = STATUS_COLORS[status] || '#607d8b';
                      
                      const colTasks = uniqueTasks.filter(t => t.display_status === status).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                      return (
                        <Box key={status} sx={{ flex: '0 0 300px', border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: 'background.paper', flexShrink: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: statusColor }} />
                              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>{statusLabel}</Typography>
                              <Chip size="small" label={colTasks.length} />
                            </Stack>
                          </Box>
                          <Stack spacing={1} sx={{ p: 1.5, flex: 1, overflowY: 'auto' }}>
                            {colTasks.map(task => (
                              <Paper
                                key={task.id}
                                sx={{
                                  p: 1.25,
                                  cursor: 'pointer',
                                  border: '1px solid',
                                  borderColor: STATUS_BORDERS[status] || '#e0e0e0',
                                  bgcolor: STATUS_TINTS[status] || '#fafafa',
                                  transition: 'all 0.15s',
                                  '&:hover': { boxShadow: 2, bgcolor: 'background.paper' },
                                  flexShrink: 0
                                }}
                                onClick={() => setDetailRow(task)}
                              >
                                <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3, mb: 0.5 }}>
                                  {task.title}
                                </Typography>
                                {task.recommendation && (
                                  <Box
                                    sx={{
                                      mb: 0.75,
                                      px: 0.8,
                                      py: 0.65,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: TASK_SIGNAL_STYLES[task.recommendation_tone || 'neutral'].border,
                                      bgcolor: TASK_SIGNAL_STYLES[task.recommendation_tone || 'neutral'].bg,
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ display: 'block', color: TASK_SIGNAL_STYLES[task.recommendation_tone || 'neutral'].color, lineHeight: 1.35, fontWeight: 600 }}>
                                      {task.recommendation}
                                    </Typography>
                                  </Box>
                                )}
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                                  {task.references?.brand && <Chip size="small" label={`Brand: ${task.references.brand}`} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                                  {task.references?.app && <Chip size="small" label={`App: ${task.references.app}`} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                                  {task.references?.plugin && <Chip size="small" label={`Plugin: ${task.references.plugin}`} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip size="small" label={task.agent_ref} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    {task.models?.length > 0 && <Chip size="small" label={summarizeModels(task.models)} variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                                    {task.correction_count > 0 && <Chip size="small" label={formatCorrectionLabel(task.correction_count)} sx={{ fontSize: '0.7rem', bgcolor: '#f3e9df' }} />}
                                    {task._historyCount > 1 && <Chip size="small" label={`${task._historyCount} updates`} sx={{ fontSize: '0.7rem' }} />}
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(task.timestamp).toLocaleDateString()} {new Date(task.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </Typography>
                                </Stack>
                                {scopeLabel && (
                                  <Chip size="small" label={scopeLabel} variant="outlined" sx={{ mt: 0.75, fontSize: '0.7rem' }} />
                                )}
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Stack>
        );
      })()}

      {tab === 2 && (() => {
        // Stand Up tab - reorganized with left nav and right side to do list
        const standup = normalizedRows
          .filter(r => r.status === 'in-progress' || r.status === 'blocked')
          .sort((a, b) => (b.status === 'blocked' ? 1 : 0) - (a.status === 'blocked' ? 1 : 0))
          .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
        
        const byProject = {};
        standup.forEach(r => {
          if (!byProject[r.project]) byProject[r.project] = [];
          byProject[r.project].push(r);
        });

        // Initialize selectedProject on first render
        if (!selectedStandUpProject && Object.keys(byProject).length > 0) {
          setSelectedStandUpProject(Object.keys(byProject)[0]);
        }

        const selectedTasks = selectedStandUpProject ? byProject[selectedStandUpProject] : [];

        return (
          <Box sx={{ display: 'flex', gap: 2, minHeight: 600 }}>
            {/* Left Nav Panel */}
            <Box sx={{ width: 200, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto' }}>
              <Stack spacing={0.5} sx={{ p: 1 }}>
                {Object.entries(byProject).map(([project, tasks]) => (
                  <Box
                    key={project}
                    onClick={() => setSelectedStandUpProject(project)}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: selectedStandUpProject === project ? 'primary.light' : 'transparent',
                      border: '1px solid',
                      borderColor: selectedStandUpProject === project ? 'primary.main' : 'divider',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>{project}</Typography>
                    <Chip size="small" label={tasks.length} sx={{ mt: 0.5 }} />
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Right Side To Do List */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {selectedTasks.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No tasks selected or no active/blocked tasks</Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={700}>{selectedStandUpProject}</Typography>
                  {selectedTasks.map((task) => (
                    <Paper
                      key={task.id}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: task.status === 'blocked' ? '#ffcdd2' : '#e3f2fd',
                        bgcolor: task.status === 'blocked' ? '#fff5f5' : '#f9fafb',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { boxShadow: 2, bgcolor: 'background.paper' }
                      }}
                      onClick={() => setDetailRow(task)}
                    >
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                          <Chip
                            size="small"
                            label={task.status}
                            sx={{
                              bgcolor: STATUS_COLORS[task.status] || '#607d8b',
                              color: '#fff',
                              fontWeight: 600
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {fmtDate(task.timestamp)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          <Chip size="small" label={task.agent_ref} variant="outlined" />
                          {task.deploy_related && <Chip size="small" label="Deploy" color="primary" />}
                        </Stack>
                      </Stack>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, lineHeight: 1.5 }}>
                        {task.title}
                      </Typography>
                      {task.summary && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                          {task.summary.slice(0, 200)}{task.summary.length > 200 ? '…' : ''}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        );
      })()}


      {/* PLANNER MODAL */}
      <Drawer anchor="right" open={plannerOpen} onClose={() => setPlannerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, display: 'flex', flexDirection: 'column' } }}>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Planner</Typography>
            <IconButton onClick={() => setPlannerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
          <Tabs value={plannerTab} onChange={(_, v) => setPlannerTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Planned Work" />
            <Tab label="Resume Context" />
            <Tab label={`Master Tasks (${aphMasterRows.length})`} />
          </Tabs>
          {plannerTab === 0 && (
            <Box>
              <Button startIcon={<AddTaskIcon />} size="small" variant="outlined" sx={{ mb: 1.5 }} onClick={() => setPlanItems(prev => [{ id: `p-${Date.now()}`, title: 'New work item', owner: 'Me', status: 'todo', due: '', notes: '' }, ...prev])}>Add Work Item</Button>
              <Stack spacing={1}>
                {planItems.map(item => (
                  <Card key={item.id} variant="outlined">
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}><TextField fullWidth size="small" label="Work item" value={item.title} onChange={e => updatePlan(item.id, 'title', e.target.value)} /></Grid>
                        <Grid item xs={6}><TextField fullWidth size="small" label="Owner" value={item.owner} onChange={e => updatePlan(item.id, 'owner', e.target.value)} /></Grid>
                        <Grid item xs={3}><FormControl fullWidth size="small"><InputLabel>Status</InputLabel><Select value={item.status} label="Status" onChange={e => updatePlan(item.id, 'status', e.target.value)}>{['todo', 'in-progress', 'done', 'blocked'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
                        <Grid item xs={3}><TextField fullWidth size="small" type="date" label="Due" InputLabelProps={{ shrink: true }} value={item.due} onChange={e => updatePlan(item.id, 'due', e.target.value)} /></Grid>
                        <Grid item xs={12}><TextField fullWidth size="small" label="Notes" multiline minRows={2} value={item.notes} onChange={e => updatePlan(item.id, 'notes', e.target.value)} /></Grid>
                        <Grid item xs={12}><IconButton size="small" color="error" onClick={() => setPlanItems(prev => prev.filter(p => p.id !== item.id))}><CloseIcon fontSize="small" /> Remove</IconButton></Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
          {plannerTab === 1 && (
            <Stack>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Resume Context</Typography>
              <TextField multiline minRows={12} fullWidth placeholder="Where you stopped and next action..." value={resumeContext} onChange={e => setResumeContext(e.target.value)} />
            </Stack>
          )}
          {plannerTab === 2 && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">APH Master Task List</Typography>
              {aphGoalRows.map(([project, projectRows]) => (
                <Paper key={project} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{project}</Typography>
                    <Chip size="small" label={projectRows.length} />
                  </Stack>
                  <Stack spacing={0.75}>
                    {projectRows.slice(0, 20).map(task => {
                      const displayStatus = deriveDisplayStatus(task);
                      return (
                        <Box key={task.id} sx={{ p: 1, borderRadius: 1, bgcolor: STATUS_TINTS[displayStatus] || '#f5f5f5', border: '1px solid', borderColor: STATUS_BORDERS[displayStatus] || '#e0e0e0' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600}>{task.title}</Typography>
                            <Chip size="small" label={displayStatus} sx={{ bgcolor: STATUS_COLORS[displayStatus] || '#607d8b', color: '#fff' }} />
                          </Stack>
                          {task.summary && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', whiteSpace: 'pre-wrap' }}>{task.summary}</Typography>}
                        </Box>
                      );
                    })}
                    {projectRows.length > 20 && <Typography variant="caption" color="text.secondary">+{projectRows.length - 20} more tasks</Typography>}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      <DetailDrawer row={detailRow} onClose={() => setDetailRow(null)} effectiveBrands={effectiveBrands} />
    </Box>
  );
}
